import { Component, DestroyRef, PLATFORM_ID, TransferState, computed, inject, makeStateKey, signal } from '@angular/core';
import { isPlatformBrowser, NgTemplateOutlet } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { DomSanitizer } from '@angular/platform-browser';
import { ActivatedRoute, ParamMap, Router, RouterLink } from '@angular/router';
import {
  Observable,
  catchError,
  distinctUntilChanged,
  forkJoin,
  map,
  of,
  shareReplay,
  switchMap,
} from 'rxjs';

import { SAVING_ARENA_API_CONFIG } from '../../../core/api/api.config';
import {
  FrontendBlog,
  FrontendCoupon,
  FrontendStoreDiscountPageLink,
  FrontendFaq,
  FrontendProduct,
  FrontendStore,
  FrontendStoreSeoSettings,
} from '../../../core/api/api.models';
import { blogBodyText, blogReadMinutes } from '../../../core/api/blog-text';
import { SavingArenaApiService } from '../../../core/api/saving-arena-api.service';
import { TrustedCmsHtmlPipe } from '../../../core/api/trusted-cms-html.pipe';
import { OfferModal, OfferModalData } from '../offer-modal/offer-modal';
import { ProductCard } from '../../../shared/product-card/product-card';
import { CouponCard } from '../../../shared/coupon-card/coupon-card';
import { OfferCard } from '../../../shared/offer-card/offer-card';
import { CouponCardView } from '../../../shared/coupon-card/coupon-card.models';
import { isCouponExpired, toCouponCardView } from '../../../shared/coupon-card/coupon-card.mapper';
import { SectionHeading } from '../../../shared/section-heading/section-heading';
import { StoreLogo } from '../../../shared/store-logo/store-logo';
import { PreconnectService } from '../../../core/perf/preconnect.service';
import { AffiliateLinkService } from '../../../core/affiliate/affiliate-link.service';
import { SeoService } from '../../../core/seo/seo.service';
import { SsrResponseService } from '../../../core/seo/ssr-response.service';
import { breadcrumbSchemaFor, breadcrumbTrail } from '../../../core/seo/breadcrumb-trail';
import {
  faqPageSchema,
  itemListSchema,
  offerSchema,
  webPageSchema,
} from '../../../core/seo/structured-data';
import { Breadcrumb } from '../../../shared/breadcrumb/breadcrumb';
import { formatCompactCount } from '../../../core/format/compact-count';

const DEFAULT_STORE_SLUG = 'zaful-discount-codes';
const COMPETITOR_LIMIT = 18;
// 15 = three full rows of the five-across partner grid.
const PARTNER_COUPON_LIMIT = 15;
const PARTNER_COUPON_FETCH_LIMIT = 64;
// 8 = two full rows of the four-across popular-stores grid.
const POPULAR_STORE_LIMIT = 8;
// One page of the store's product grid. 24 fills whole rows at every `.sav-product-grid`
// breakpoint (2 / 3 / 4 / 6 columns), so the last row is never left ragged.
const STORE_PRODUCT_LIMIT = 24;
// One full row at every `.sav-product-grid` breakpoint, same as above — the savings row is a
// highlight strip, not a browsable list, so it stays at a single page with no "load more".
const STORE_TOP_SAVINGS_LIMIT = 12;
const STORE_BLOG_LIMIT = 9;
const STORE_ABOUT_FALLBACK = 'More details about this store will be available soon.';
const STORE_LONGFORM_FALLBACK =
  'Detailed savings information for this store will be available soon.';

/**
 * The card view model now lives with the shared `CouponCard` component, because the store discount
 * pages render the identical card and the two must not drift. The alias is kept so the rest of this
 * file reads unchanged.
 */
type StoreCouponCard = CouponCardView;

interface StoreFaqItem {
  question: string;
  answer: string;
}

/** Colour scheme of the event ribbon on the hero logo; picked from the event's slug. */
type StoreEventRibbonTheme = 'halloween' | 'black-friday' | 'cyber-monday' | 'singles-day' | 'christmas' | 'default';

/** The running event this store has a live offer in, shown as a ribbon across the hero logo. */
interface StoreEventRibbon {
  name: string;
  theme: StoreEventRibbonTheme;
}

/** A "Compare Similar Stores" card — always backed by a real published comparison page. */
/**
 * One head-to-head card in "Compare Similar Stores". The card shows both sides of the pair, so it
 * carries this store's figures as well as the rival's — all of it already on the `/comparisons`
 * summary, which the old card fetched and threw away in favour of a boilerplate sentence.
 */
interface StoreComparisonCard {
  name: string;
  initials: string;
  logoUrl: string;
  /** Rival's active coupon count; `null` when the API did not report one. */
  offers: number | null;
  /** This store's side of the pair, so the card can render an actual comparison. */
  selfName: string;
  selfInitials: string;
  selfLogoUrl: string;
  selfOffers: number | null;
  /** Leading category of the pair, or '' when neither store has one. */
  categoryName: string;
  /** Admin-computed winner. '' on a tie — the card never invents one. */
  winnerName: string;
  /** True when this store is the winner, for emphasis on the right side of the versus block. */
  selfWins: boolean;
  comparisonPath: string;
  storePath: string;
}

/** One tile in the "<Category> Popular Stores" row. */
interface PopularStoreItem {
  id: string;
  name: string;
  logoUrl: string;
  logoInitials: string;
  path: string;
  /** e.g. "40+ Coupons & Deals"; '' when the store has none, so the line is dropped. */
  countLabel: string;
}

interface StoreCompetitorItem {
  id: string;
  slug: string;
  name: string;
  domain: string;
  /**
   * What this store actually offers, rather than prose about it. The card used to carry a
   * paragraph-long description, which pushed the row to several screens and told a shopper
   * comparing stores nothing they could act on — these numbers are the reason to click through.
   */
  couponsCount: number;
  dealsCount: number;
  rating: number;
  ratingCount: number;
  /**
   * The card's whole line, written out: "Foo offers 4 deals with savings of up to 43% off and a
   * 4.3/5 rating from 153 reviews." Built from this store's own figures, so no two cards read alike.
   */
  summary: string;
  logoUrl: string;
  logoInitials: string;
  path: string;
}

interface StorePartnerCouponItem {
  id: string;
  storeId: string;
  storeSlug: string;
  storeName: string;
  storeLogoUrl: string;
  storeLogoInitials: string;
  title: string;
  actionLabel: string;
  isDeal: boolean;
  /** Display expiry, already stripped of its "Expires:" prefix for the shared offer card. */
  expires: string;
  /** Last 3 characters of the code, for the card's "Reveal Code" affordance. */
  codeSuffix: string;
  path: string;
}

interface StoreProductItem {
  id: string;
  title: string;
  description: string;
  imageUrl: string;
  /** A genuinely different angle from the feed's gallery; '' when the merchant supplied only one shot. */
  hoverImageUrl: string;
  price: string;
  oldPrice: string;
  path: string;
  /** Merchant/affiliate URL for the card's "Buy Now" action; '' when neither the product nor its store has one. */
  buyHref: string;
  tag: string;
  isLimited: boolean;
}

interface StoreBlogItem {
  id: string;
  title: string;
  excerpt: string;
  imageUrl: string;
  imageAlt: string;
  storeLogoUrl: string;
  path: string[];
  categoryName: string;
  categoryPath: string[];
  authorName: string;
  authorPath: string[];
  readTime: string;
}

interface StoreMembershipFeature {
  text: string;
  muted: boolean;
}

interface StoreMembershipPlanItem {
  id: string;
  name: string;
  description: string;
  price: string;
  period: string;
  /** The merchant's own discount label for the plan ("50% OFF"), shown beside the price. */
  discount: string;
  features: StoreMembershipFeature[];
  buttonText: string;
  footnote: string;
  badge: string;
  featured: boolean;
  /** Where the button goes: the store's `/go/s/` click-out, or the plan's own affiliate link. */
  href: string;
}

interface StoreMembershipSection {
  heading: string;
  description: string;
  plans: StoreMembershipPlanItem[];
}

interface StoreSidebarWidgetItem {
  id: string;
  title: string;
  text: string;
}

type StoreSupportIcon = 'shipping' | 'returns' | 'payment' | 'default';
type StoreSocialIcon =
  'facebook' | 'instagram' | 'x' | 'youtube' | 'linkedin' | 'pinterest' | 'default';

interface StoreSupportLinkItem {
  id: string;
  title: string;
  description: string;
  url: string;
  icon: StoreSupportIcon;
}

interface StoreSocialLinkItem {
  id: string;
  url: string;
  label: string;
  icon: StoreSocialIcon;
}

interface StoreInternalLinkItem {
  id: string;
  name: string;
  path: string;
  logoUrl: string;
  logoInitials: string;
  couponCountText: string;
}

type ApiRecord = Record<string, unknown>;

// The admin who last updated the store — shown in the "Reviewed by" card.
interface PageEditor {
  name: string;
  role: string;
  bio: string;
  avatarUrl: string;
  initials: string;
  profileUrl: string;
}

// A published store review (approved StoreTestimonial) shown in the reviews carousel.
interface StoreReviewItem {
  id: string;
  title: string;
  text: string;
  name: string;
  designation: string;
  initials: string;
  dateLabel: string;
  rating: number;
  stars: number[];
}

@Component({
  selector: 'app-store-page',
  imports: [
    NgTemplateOutlet,
    Breadcrumb,
    RouterLink,
    TrustedCmsHtmlPipe,
    ReactiveFormsModule,
    OfferModal,
    ProductCard,
    CouponCard,
    OfferCard,
    SectionHeading,
    StoreLogo,
  ],
  templateUrl: './store-page.html',
  styleUrl: './store-page.scss',
  host: { '(document:keydown.escape)': 'onEscape()' },
})
export class StorePage {
  private readonly api = inject(SavingArenaApiService);
  private readonly apiConfig = inject(SAVING_ARENA_API_CONFIG);
  private readonly destroyRef = inject(DestroyRef);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly formBuilder = inject(FormBuilder);
  private readonly sanitizer = inject(DomSanitizer);
  private readonly seo = inject(SeoService);
  private readonly affiliate = inject(AffiliateLinkService);
  private readonly preconnect = inject(PreconnectService);
  private readonly ssrResponse = inject(SsrResponseService);
  private readonly isBrowser = isPlatformBrowser(inject(PLATFORM_ID));
  private readonly transferState = inject(TransferState);
  private relatedDataRequestId = 0;

  readonly storeName = 'Zaful';
  private readonly aboutPreviewLimit = 300;
  private readonly sidebarPreviewLimit = 300;
  private readonly longformPreviewLimit = 500;
  readonly aboutStoreDescription =
    'Zaful is a global fashion destination known for trend-driven collections, fast product drops, and budget-friendly pricing. The brand focuses on modern silhouettes, seasonal essentials, and everyday staples for shoppers who want style without overspending. Customers can usually find rotating promotions, welcome offers, and bundle savings across categories such as dresses, activewear, streetwear, and accessories. With frequent campaign updates and wide inventory refreshes, Zaful remains a popular option for value-conscious online fashion shoppers.';
  readonly zafulShippingDescription =
    'Zaful shipping options vary by destination, order value, and ongoing campaigns. Standard delivery is usually the default choice, while expedited options may be available in select regions. During promotional periods, free shipping thresholds can reduce total order costs significantly, especially when combined with verified coupon codes. Delivery timelines can differ by warehouse location and product availability, so it is best to review estimated delivery windows on the checkout page before placing your order.';
  readonly zafulReturnsDescription =
    'Zaful returns are generally easier when items are kept in original condition with tags and packaging intact. Return eligibility may depend on product category, hygiene restrictions, and local policy terms. If an item does not fit as expected, start the return request promptly through your account dashboard and follow the provided instructions carefully. Reading policy details before checkout helps avoid surprises and makes discount shopping safer, especially when purchasing new styles or unfamiliar sizing.';
  readonly zafulClearanceDescription =
    'Zaful clearance sales are a strong source of value for shoppers who prioritize final price over trend timing. These deals often include end-of-season inventory, overstock items, and rotating markdown collections across multiple categories. Since availability can change quickly, the best approach is to shortlist products early and complete checkout once your target discount appears. Pairing clearance pricing with active sitewide promotions can unlock deeper savings, but eligibility rules may vary by campaign and product type.';

  isShippingExpanded = false;
  isReturnsExpanded = false;
  isClearanceExpanded = false;

  protected readonly store = signal<FrontendStore | null>(null);

  protected readonly couponCards = signal<StoreCouponCard[]>([]);
  // "Load more" pagination for the coupon list. The initial batch (50) comes from /stores/:slug;
  // further pages come from the paginated /coupons endpoint (see loadMoreCoupons).
  private readonly PAGE_SIZE = 50;
  private readonly loadedCouponIds = new Set<string>();
  private readonly rawCouponsLoaded = signal(0);
  private readonly couponsFetchPage = signal(1);
  protected readonly loadingMoreCoupons = signal(false);
  protected readonly expiredCouponCards = signal<StoreCouponCard[]>([]);
  /** The offer whose modal is open (driven by the `?coupon=` URL param); null when closed. */
  protected readonly activeOffer = signal<OfferModalData | null>(null);
  protected readonly faqItems = signal<StoreFaqItem[]>([]);
  protected readonly competitorStores = signal<StoreCompetitorItem[]>([]);
  /** Latest stores in this store's primary category — the "<Category> Popular Stores" row. */
  protected readonly popularStores = signal<PopularStoreItem[]>([]);
  protected readonly popularStoresHeading = computed(() => {
    const category = this.store()?.categories?.[0]?.name?.trim() || 'Popular';
    const today = new Date().toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    });
    return `${category} Popular Stores ${today}`;
  });
  protected readonly partnerCoupons = signal<StorePartnerCouponItem[]>([]);
  protected readonly storeProducts = signal<StoreProductItem[]>([]);
  // Deepest markdowns for this store, ranked server-side by `discountPercent` — a separate call
  // from the grid above, which is ordered by recency and would rarely surface the best prices.
  protected readonly topSavingsProducts = signal<StoreProductItem[]>([]);
  // "Load more" pagination for the product grid, mirroring the coupon list above: a big store can
  // have thousands of products, so they arrive a page at a time instead of all at once.
  private readonly loadedProductIds = new Set<string>();
  private readonly rawProductsLoaded = signal(0);
  private readonly productsFetchPage = signal(1);
  protected readonly productsTotal = signal(0);

  /**
   * Which page of this store's products the URL asked for. 1 is the bare `/<store-slug>` URL.
   *
   * This exists because the grid renders 24 products and everything past that used to sit behind a
   * click handler, which a crawler cannot fire. A store with 1,867 products therefore exposed 24 of
   * them and orphaned the rest — 39,865 product pages site-wide were unreachable for exactly this
   * reason. The `?page=N` URLs below are server-rendered and linked with real anchors, so the whole
   * catalogue is walkable without JavaScript. The Load more button stays for people.
   */
  protected readonly currentProductPage = signal(1);

  /** True when this render is answering with a 301, so it must not also try to be a 404. */
  private productPageRedirected = false;

  protected readonly productTotalPages = computed(() => {
    const total = this.productsTotal();
    return total > 0 ? Math.ceil(total / STORE_PRODUCT_LIMIT) : 1;
  });

  /**
   * Page numbers to render as links: first, last, and a window around the current page, with `null`
   * standing for a gap. Mirrors /storelist so the two paginations behave identically.
   *
   * Every page stays reachable by walking prev/next, so a crawler loses nothing — but Vita Springs
   * has 8,280 products, i.e. 345 pages, and putting 345 links in the footer of all 345 of them would
   * trade one crawl problem for a worse one.
   */
  protected readonly productPageLinks = computed<(number | null)[]>(() => {
    const total = this.productTotalPages();
    const current = this.currentProductPage();
    if (total <= 1) return [];
    if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);

    const wanted = new Set([1, total, current, current - 1, current + 1]);
    const shown = [...wanted].filter((n) => n >= 1 && n <= total).sort((a, b) => a - b);

    const out: (number | null)[] = [];
    let previous = 0;
    for (const n of shown) {
      if (previous && n - previous > 1) out.push(null);
      out.push(n);
      previous = n;
    }
    return out;
  });

  /** Query params for a page link. Page 1 is the bare URL, so it carries none. */
  protected productPageQuery(page: number): Record<string, string> {
    return page <= 1 ? {} : { page: String(page) };
  }

  /**
   * The route target for a pagination link — this store's own one-segment URL.
   *
   * Taken from the resolved store rather than the route param: this component is also mounted by
   * RootSlugPage, where the param is the bare slug, and by the `/coupon/:id` deep link. The store's
   * own slug is the one value that is correct in every case.
   */
  protected readonly storeRouterLink = computed(() => {
    const slug = this.store()?.slug || this.route.snapshot.paramMap.get('storeSlug') || '';
    return ['/', slug];
  });
  protected readonly loadingMoreProducts = signal(false);
  protected readonly storeBlogs = signal<StoreBlogItem[]>([]);
  protected readonly comparisonCards = signal<StoreComparisonCard[]>([]);
  /** This store's published discount pages (Student / Teacher / …), linked from the store page. */
  protected readonly discountPageLinks = signal<FrontendStoreDiscountPageLink[]>([]);
  protected readonly isStoreLoading = signal(true);

  // Store rating widget state. average/count come from the store payload; hasRated tells us this
  // IP already voted (server-enforced). hoverRating previews the star under the cursor.
  protected readonly ratingStars = [1, 2, 3, 4, 5] as const;
  protected readonly ratingAverage = signal(0);
  protected readonly ratingCount = signal(0);
  protected readonly hasRated = signal(false);
  protected readonly hoverRating = signal(0);
  protected readonly submittingRating = signal(false);
  protected readonly ratingError = signal('');
  protected readonly justRated = signal(false);
  // How many stars to paint solid: the hovered value while hovering, otherwise the rounded average.
  protected readonly activeStars = computed(() =>
    this.hoverRating() > 0 ? this.hoverRating() : Math.round(this.ratingAverage()),
  );

  // --- Follow (email capture) ---
  protected readonly isFollowOpen = signal(false);
  protected readonly followSubmitting = signal(false);
  protected readonly followed = signal(false);
  // Badge figures for the Follow / Share buttons: the store's real activity plus its fixed starting
  // number, both computed by the API. Seeded from the store payload, then updated from whatever the
  // follow/share endpoints return.
  protected readonly followerCount = signal(0);
  protected readonly shareCount = signal(0);
  protected readonly shareLabel = signal('Share');
  protected readonly followError = signal('');
  protected readonly followForm = this.formBuilder.group({
    email: ['', [Validators.required, Validators.email]],
    companyWebsite: [''], // honeypot
  });

  // --- Leave a review ---
  protected readonly reviewRatingStars = [1, 2, 3, 4, 5] as const;
  protected readonly isReviewOpen = signal(false);
  protected readonly reviewSubmitting = signal(false);
  protected readonly reviewSubmitted = signal(false);
  protected readonly reviewError = signal('');
  protected readonly reviewHover = signal(0);
  protected readonly reviewForm = this.formBuilder.group({
    userName: ['', [Validators.required]],
    userDesignation: [''],
    title: ['', [Validators.required]],
    description: ['', [Validators.required]],
    rating: [5, [Validators.required]],
    companyWebsite: [''], // honeypot
  });
  protected readonly reviewActiveStars = computed(() =>
    this.reviewHover() > 0 ? this.reviewHover() : (this.reviewForm.controls.rating.value ?? 0),
  );

  // Published reviews (approved testimonials) for the "What shoppers say" carousel.
  protected readonly reviewItems = computed<StoreReviewItem[]>(() =>
    this.toReviewItems(this.store()?.testimonials),
  );
  protected readonly reviewsCount = computed(() => this.reviewItems().length);
  protected readonly reviewsAverage = computed(() => {
    const items = this.reviewItems();
    if (items.length === 0) {
      return 0;
    }
    const sum = items.reduce((total, item) => total + item.rating, 0);
    return Math.round((sum / items.length) * 10) / 10;
  });
  protected readonly reviewsScoreStars = computed(() => Math.round(this.reviewsAverage()));

  // Admin profile for the "Reviewed by" card (store.updatedBy → serialized).
  protected readonly pageEditor = computed<PageEditor | null>(() =>
    this.toPageEditor(this.store()?.['updatedByUser']),
  );

  protected readonly membershipSection = computed(() => this.toMembershipSection(this.store()));
  protected readonly sidebarWidgetItems = computed(() =>
    this.toSidebarWidgetItems(this.store()?.sidebarWidgets ?? []),
  );
  private readonly socialProfileRecord = computed(() =>
    this.toRecord(this.store()?.['socialProfile']),
  );
  protected readonly supportLinkItems = computed(() =>
    this.toSupportLinkItems(this.socialProfileRecord()?.['supportLinks']),
  );
  protected readonly contactPhone = computed(() =>
    this.getString(this.socialProfileRecord(), ['phone']),
  );
  protected readonly contactPhoneHref = computed(() => {
    const phone = this.contactPhone();

    return phone ? `tel:${phone.replace(/[^+\d]/g, '')}` : '';
  });
  protected readonly contactAddress = computed(() =>
    this.getString(this.socialProfileRecord(), ['address']),
  );
  protected readonly contactEmail = computed(() =>
    this.getString(this.socialProfileRecord(), ['email']),
  );
  protected readonly contactEmailHref = computed(() => {
    const email = this.contactEmail();

    return email ? `mailto:${email}` : '';
  });
  protected readonly socialLinkItems = computed(() =>
    this.toSocialLinkItems(this.socialProfileRecord()?.['socialLinks']),
  );
  protected readonly storeVideoId = computed(() =>
    this.getString(this.socialProfileRecord(), ['videoId']),
  );
  protected readonly storeVideoThumbUrl = computed(() => {
    const id = this.storeVideoId();

    return id ? `https://i.ytimg.com/vi/${id}/hqdefault.jpg` : '';
  });
  protected readonly storeVideoEmbedUrl = computed(() => {
    const id = this.storeVideoId();

    return id
      ? this.sanitizer.bypassSecurityTrustResourceUrl(
          `https://www.youtube-nocookie.com/embed/${id}?autoplay=1&rel=0`,
        )
      : null;
  });
  protected readonly isVideoActivated = signal(false);

  protected activateVideo(): void {
    this.isVideoActivated.set(true);
  }
  protected readonly internalLinkStoreItems = computed(() =>
    this.toInternalLinkStoreItems(this.store()?.['internalLinks']),
  );
  protected readonly hasStoreContactSection = computed(
    () =>
      Boolean(this.contactPhone() || this.contactAddress() || this.contactEmail()) ||
      this.socialLinkItems().length > 0,
  );
  protected readonly hasCustomerCareSection = computed(
    () => this.supportLinkItems().length > 0 || this.hasStoreContactSection(),
  );
  protected readonly storeDisplayName = computed(() => this.store()?.name || this.storeName);

  protected readonly crumbs = computed(() =>
    breadcrumbTrail({ label: 'Stores', path: '/storelist' }, { label: this.storeDisplayName() }),
  );
  /**
   * The deepest discount this store's live coupons advertise, as a whole percentage — the "61" in
   * "Cut Your Cost by Up to 61% with a Longer Plan". 0 when not one of them quotes a percentage.
   *
   * Read out of the coupon titles because that is the only place it exists: a coupon carries no
   * discount field, so the number is written into the name by whoever loaded it.
   *
   * "% back" and "% cashback" are skipped deliberately. Those are a rebate paid afterwards, not
   * money off the price, and a store whose best offer is "Earn 5% Back" must not have an H1 that
   * promises "5% Off".
   *
   * A signal set once from the first batch of coupons, NOT a computed over `couponCards()`: that
   * list grows when the reader presses "Load more", and a page's H1 must not rewrite itself
   * mid-scroll because page two happened to carry a deeper offer. The number the page is served
   * with is the number it keeps.
   */
  protected readonly maxCouponDiscountPercent = signal(0);

  /**
   * Fallback wording from the admin (Settings > Store SEO Fallbacks); null until it has loaded, or
   * when nothing has been saved there. Either way the built-in templates below take over.
   */
  private readonly storeSeoFallbacks = signal<FrontendStoreSeoSettings | null>(null);

  /**
   * These templates are global, but this component reloads on every in-app navigation between
   * stores, so the request is shared and replayed rather than repeated 40 times as someone browses.
   */
  private readonly storeSeoSettings$ = this.api.getStoreSeoSettings().pipe(
    map((response) => (response?.success ? response.data : null)),
    catchError(() => of(null)),
    shareReplay({ bufferSize: 1, refCount: false }),
  );

  /**
   * The wording a store page falls back to, when the admin has saved none of its own.
   *
   * `{keyword}` is the store's OWN secondary keyword — the phrase chosen when the store was added,
   * the same one its slug ends in ("coupon codes" for `faphouse-coupon-codes`, "kampanjekode" for
   * `viking-footwear-kampanjekode`). So a Norwegian store's title says "kampanjekode" rather than an
   * English phrase picked here.
   *
   * The two differ only in where the discount sits: first in the title, after the name in the H1.
   * Both are the documented defaults in `docs/API.md`; change them there too.
   */
  private readonly DEFAULT_TITLE_TEMPLATE = '{discount} {store} {keyword} | {month} {year}';
  private readonly DEFAULT_HEADING_TEMPLATE = '{store} {discount} {keyword} — {month} {year}';

  /** Used for `{keyword}` when a store has no secondary keyword of its own. */
  private readonly DEFAULT_SECONDARY_KEYWORD = 'Coupon Codes';

  /**
   * The H1 when the admin has not written one for this store.
   *
   * It has to stand on its own rather than read as a placeholder: sampling the catalogue, almost no
   * store has `headingH1` filled in, so this is the heading nearly every store page ships with.
   *
   * The wording itself is a template the admin can edit (Settings > Store SEO Fallbacks); see
   * `DEFAULT_HEADING_TEMPLATE` for what it is when they have not.
   */
  private readonly fallbackStoreHeading = computed(() =>
    this.renderStoreSeoTemplate(this.storeSeoTemplate('headingFallback')),
  );

  protected readonly storeHeading = computed(() => {
    const heading = this.getContentString(this.store()?.content, 'headingH1');

    return heading || this.fallbackStoreHeading();
  });

  /**
   * The admin's H2 for this store (Stores > Content > H2 Heading).
   *
   * It was being sent to the browser and never rendered — 442 of the 1,184 content records fill it
   * in, and on every one of them the text simply did not appear. Because most admins write an H2
   * close to their H1, the page looked as though the H2 had been promoted into the H1 slot; in fact
   * the H1 field was rendering and this one was dropped.
   *
   * Left out when empty: a heading with no text is worse than no heading, both for the outline and
   * for the gap it leaves under the title.
   */
  protected readonly storeSubheading = computed(() =>
    this.getContentString(this.store()?.content, 'headingH2'),
  );

  /**
   * What fraction of its column one line of the H1 needs, fed to the `.sav-right-col > h1` ramp as
   * `--sav-h1-fit` so the heading is sized to fit on a single line.
   *
   * CSS cannot measure text, so the ramp used to size the H1 off the viewport alone — which works
   * only while every heading is about the same length. It is not: an admin heading may be three
   * words, while the generated fallback is the store name plus a discount, a keyword and a date.
   * The heading's own length is the missing half of that sum, and it is the half only the component
   * knows.
   */
  protected readonly storeHeadingFit = computed(() => {
    // +2 for the verified badge trailing the last word (0.9em plus its 8px margin).
    const characters = this.storeHeading().length + 2;
    // Average advance width of DM Sans Bold over mixed-case text and digits, in em. Rounded up from
    // the ~0.58 it actually measures, so the estimate errs towards a slightly small heading rather
    // than a wrapped one.
    const averageCharacterWidth = 0.62;

    return Number((1 / (characters * averageCharacterWidth)).toFixed(5));
  });
  /**
   * The store's own affiliate link, for the logo click-out.
   *
   * Falls back to `storeUrl` so a store without an affiliate programme still goes somewhere useful
   * rather than the logo being dead. Empty when neither is set, and the template then renders the
   * badge unlinked instead of an anchor to nowhere.
   */
  protected readonly storeAffiliateUrl = computed(() => {
    const store = this.store();
    const affiliate = (store?.affiliateLink ?? '').trim();
    const fallback = (store?.storeUrl ?? '').trim();

    return affiliate || fallback;
  });

  /**
   * What the logo actually links to: the `/go/s/:slug` redirect, never the merchant URL.
   *
   * The logo used to carry the raw affiliate link in its `href`. That made it the one click-out on
   * the site whose destination a visitor could read off the status bar or copy out of the page
   * source, and the one that produced no click record — every other CTA here already goes through
   * `/go/`. The redirect resolves the same destination server-side and logs the click.
   *
   * Empty when the store has neither an affiliate link nor a URL, which is what keeps the template
   * rendering the badge as plain artwork rather than as an anchor to nowhere.
   */
  protected readonly storeGoUrl = computed(() => {
    if (!this.storeAffiliateUrl()) return '';

    const slug = this.route.snapshot.paramMap.get('storeSlug') || this.store()?.slug || '';
    return this.affiliate.storeUrl(slug);
  });

  protected readonly storeLogoUrl = computed(() => {
    const logoImagePath = this.store()?.logoImagePath;

    return logoImagePath ? this.toAssetUrl(logoImagePath) : '';
  });

  /**
   * The ribbon across the foot of the hero logo ("Halloween Sale", "Black Friday"): the name of an
   * event that is running right now AND that this store has an unexpired offer in. The label is a
   * promise that the page holds that event's offers, so a store merely assigned to the event, with
   * no live offer in it, gets no ribbon. Read from the first coupon batch's `event` field, so it
   * costs no extra request. When two running events qualify, the store's own event wins, then the
   * one ending first, since that is the offer with the least time left.
   */
  protected readonly eventRibbon = computed<StoreEventRibbon | null>(() => {
    const store = this.store();
    if (!store) return null;

    const now = Date.now();
    const running = new Map<string, { name: string; slug: string; end: number }>();
    for (const coupon of store.coupons ?? []) {
      const event = coupon.event;
      if (!event?.id || !event.name || isCouponExpired(coupon)) continue;
      const start = Date.parse(event.startDate ?? '');
      const end = Date.parse(event.endDate ?? '');
      if (!(start <= now && now <= end)) continue;
      running.set(event.id, { name: event.name, slug: event.slug ?? '', end });
    }
    if (!running.size) return null;

    const pick =
      running.get(store.event?.id ?? '') ?? [...running.values()].sort((a, b) => a.end - b.end)[0];
    return { name: pick.name, theme: this.eventRibbonTheme(pick.slug) };
  });
  protected readonly storeInitials = computed(() => this.getInitials(this.storeDisplayName()));

  // The hero's load-failure fallback and its name-fit maths moved into the shared app-store-logo,
  // which paints the same white-ground name for every logo slot on the site.
  /**
   * Path to this store's deals page, or '' when it does not have one. The backend only fills
   * `dealsStoreKey` for stores that clear the product threshold, so an empty value here means the
   * page genuinely does not exist and the link must stay hidden rather than 404.
   */
  protected readonly dealsPagePath = computed(() => {
    const key = (this.store()?.dealsStoreKey ?? '').trim();
    if (!key) return '';

    // `/collection/<collection>/<storeKey>`. Without a collection the legacy URL still works — the
    // deals page rewrites it to the canonical one once it knows the store.
    const collection = String(this.store()?.['dealsStoreCategorySlug'] ?? '').trim();
    return collection ? `/collection/${collection}/${key}` : `/deals/${key}`;
  });
  /**
   * Fetches the next page of this store's active coupons and appends them, so the list can reach the
   * full total shown in the header (e.g. all 606, not just the first 50). Browser-only; deduped by id.
   */
  protected loadMoreCoupons(): void {
    const store = this.store();
    if (!store || this.loadingMoreCoupons() || !this.hasMoreCoupons()) {
      return;
    }

    const storeId = String(store.id ?? '');
    if (!storeId) return;

    const nextPage = this.couponsFetchPage() + 1;
    this.loadingMoreCoupons.set(true);

    this.api
      .getCoupons({ store: storeId, page: nextPage, limit: this.PAGE_SIZE })
      .pipe(
        catchError(() => of(null)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((response) => {
        this.loadingMoreCoupons.set(false);
        if (!response?.success) return;

        const fetched = response.data ?? [];
        this.couponsFetchPage.set(nextPage);
        this.rawCouponsLoaded.update((n) => n + fetched.length);

        // Drop anything already shown (safe against any sort-boundary overlap).
        const fresh = fetched.filter((coupon) => {
          const id = this.getString(this.toRecord(coupon), ['id']);
          if (!id || this.loadedCouponIds.has(id)) return false;
          this.loadedCouponIds.add(id);
          return true;
        });
        if (!fresh.length) return;

        const newActive = this.toCouponCards(fresh, false);
        const newExpired = this.toCouponCards(fresh, true);
        if (newActive.length) this.couponCards.update((cards) => [...cards, ...newActive]);
        if (newExpired.length) this.expiredCouponCards.update((cards) => [...cards, ...newExpired]);
      });
  }

  /** True active-coupon total from the API (`activeCouponsCount`), NOT the capped `coupons` array. */
  protected readonly activeCouponsCount = computed(() => {
    const total = Number(this.store()?.['activeCouponsCount'] ?? 0);
    // Fall back to the rendered card count only if the API didn't send a total (older cache).
    return total > 0 ? total : this.couponCards().length;
  });
  protected readonly activeOffersSubtitle = computed(() => {
    // Show the REAL total, not the number of cards fetched (capped at 50). Otherwise a store with
    // 606 active coupons would read "50 active offers" and contradict the comparison page.
    const count = this.activeCouponsCount();

    return count > 0
      ? `${count} active offers available for ${this.storeDisplayName()}`
      : `Top offers for ${this.storeDisplayName()}`;
  });
  protected readonly topCoupon = computed(() => this.couponCards()[0]);
  protected readonly couponList = computed(() => this.couponCards().slice(1));
  // More coupons exist in the DB than are currently shown — the raw fetched count is still below the
  // store's true active total. (`rawCouponsLoaded` counts every coupon fetched, including any that
  // render as expired, so this never gets stuck "loading" past the real end.)
  protected readonly hasMoreCoupons = computed(
    () => Boolean(this.store()) && this.rawCouponsLoaded() < this.activeCouponsCount(),
  );

  // `rawProductsLoaded` counts every product fetched, including ones dropped for a missing title or
  // image, so this never sticks on "load more" after the last real page.
  protected readonly hasMoreProducts = computed(
    () => Boolean(this.store()) && this.rawProductsLoaded() < this.productsTotal(),
  );

  constructor() {
    // First, and before the store load below: `queryParamMap` emits synchronously on subscribe, so
    // this sets `currentProductPage` in time for the initial product fetch to ask for the right
    // page. On SSR there is no second chance — whatever is rendered is what the crawler sees.
    this.watchProductPage();

    this.route.paramMap
      .pipe(
        map((params) => params.get('storeSlug') || DEFAULT_STORE_SLUG),
        // Opening/closing the coupon modal changes `paramMap` (it adds `/coupon/:couponId`) but
        // NOT the store. Without this the whole store would be refetched every time the modal is
        // opened, throwing away the coupons the modal is about to read.
        distinctUntilChanged(),
        switchMap((slug) => {
          this.isStoreLoading.set(true);
          this.relatedDataRequestId += 1;
          this.store.set(null);
          this.couponCards.set([]);
          this.maxCouponDiscountPercent.set(0);
          this.expiredCouponCards.set([]);
          this.loadedCouponIds.clear();
          this.rawCouponsLoaded.set(0);
          this.couponsFetchPage.set(1);
          this.loadingMoreCoupons.set(false);
          this.storeProducts.set([]);
          this.loadedProductIds.clear();
          this.rawProductsLoaded.set(0);
          this.productsFetchPage.set(1);
          this.productsTotal.set(0);
          this.loadingMoreProducts.set(false);
          this.topSavingsProducts.set([]);
          this.faqItems.set([]);
          this.competitorStores.set([]);
          this.partnerCoupons.set([]);
          this.storeProducts.set([]);
          this.storeBlogs.set([]);
          this.comparisonCards.set([]);
          this.discountPageLinks.set([]);
          this.resetRatingState();
          this.resetFollowReviewState();

          // Joined rather than fetched separately: `applyStoreSeo` below needs the fallback
          // templates in hand, and on the server there is no second render to correct a title that
          // arrived late.
          return forkJoin({
            store: this.api.getStore(slug).pipe(
              catchError((error: unknown) => {
                // Both outcomes still resolve to `null` so the rest of the pipeline is unchanged,
                // but remember WHY it was null. Without this the page cannot tell "no such store"
                // from "the API did not answer", and answers 404 to both.
                this.ssrResponse.noteFetchFailure(error);
                return of(null);
              }),
            ),
            seoFallbacks: this.storeSeoSettings$,
          });
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(({ store: response, seoFallbacks }) => {
        const store = response?.success ? response.data : null;

        this.storeSeoFallbacks.set(seoFallbacks);

        this.isStoreLoading.set(false);
        this.store.set(store);
        // Warm the affiliate network's host too. A click-out hops API -> network -> merchant, and
        // that middle hop is the slowest part of the wait; its handshake can happen now instead.
        this.preconnect.warm(String(store?.affiliateLink ?? ''));
        // Badge figures arrive ready-made from the API (real activity plus this store's starting
        // number), so the Follow / Share buttons render correct numbers on first paint.
        this.followerCount.set(Number(store?.followers ?? 0));
        this.shareCount.set(Number(store?.shares ?? 0));
        this.applyRatingFromStore(store);
        const initialCoupons = store?.coupons ?? [];
        this.loadedCouponIds.clear();
        for (const coupon of initialCoupons) {
          const id = this.getString(this.toRecord(coupon), ['id']);
          if (id) this.loadedCouponIds.add(id);
        }
        this.rawCouponsLoaded.set(initialCoupons.length);
        this.couponsFetchPage.set(1);
        this.loadingMoreCoupons.set(false);
        const activeCouponCards = this.toCouponCards(initialCoupons, false);
        this.couponCards.set(activeCouponCards);
        this.maxCouponDiscountPercent.set(this.maxDiscountPercentIn(activeCouponCards));
        this.expiredCouponCards.set(this.toCouponCards(initialCoupons, true));
        this.faqItems.set(this.toFaqItems(store?.faqs ?? []));
        this.competitorStores.set([]);
        this.partnerCoupons.set([]);
        this.storeProducts.set([]);
        this.storeBlogs.set([]);

        this.applyStoreSeo(store);
        // Coupons are now loaded — honour any `/coupon/:id` deep link (direct visit / refresh).
        this.syncOfferFromQuery();

        if (store) {
          this.loadRatingState(store.slug || this.storeName);
          this.loadRelatedStoreData(store);
          this.loadComparisonCards(store);
          this.loadDiscountPageLinks(store);
        } else {
          this.relatedDataRequestId += 1;
        }
      });

    // React to the coupon segment changing (direct visit, back/forward, close) without a reload.
    this.route.paramMap.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.syncOfferFromQuery();
    });
  }

  // ── Offer modal (Get Code / Get Deal) ────────────────────────────────────────────────────────

  /**
   * Get Code / Get Deal click — the same two destinations the home page's Trending Coupons use, so
   * a coupon behaves identically wherever it is clicked:
   *  - a NEW tab gets the offer URL `/<store>/coupon/<id>`, which this page turns into the popup, so
   *    the code stays on screen and the page can be shared;
   *  - THIS tab follows the `/go/:store/:id` redirect to the merchant.
   *
   * Order matters. The new tab is opened first, while the browser still counts this as a user
   * gesture — set `location` first and the pending `window.open` is discarded as the page unloads.
   * Opening exactly one tab per click also keeps popup blockers satisfied.
   *
   * The redirect increments the coupon's Hits counter server-side, so nothing is recorded here.
   */
  protected openOffer(card: StoreCouponCard): void {
    // No browser (SSR/prerender): fall back to opening the popup in place rather than doing nothing.
    if (!this.isBrowser) {
      this.router.navigate(['/', this.currentStoreSlug(), 'coupon', card.id]);
      return;
    }

    this.affiliate.openCoupon({
      storeSlug: this.currentStoreSlug(),
      couponId: card.id,
      store: this.storeDisplayName(),
      kind: 'coupon',
    });
  }

  /**
   * Same two-tab open for a coupon in the "partner coupons" strip, which belongs to a *different*
   * store — so both the popup URL and the redirect are built from that coupon's own store slug, not
   * this page's. Previously these CTAs just navigated to the partner's store page, so a click there
   * never reached the merchant at all.
   */
  protected openPartnerOffer(coupon: StorePartnerCouponItem, event: Event): void {
    event.preventDefault();
    if (!this.isBrowser || !coupon.id || !coupon.storeSlug) return;

    // Attributed to the partner's store, not this page's — otherwise every partner click would be
    // credited to whichever store the reader happened to be looking at.
    this.affiliate.openCoupon({
      storeSlug: coupon.storeSlug,
      couponId: coupon.id,
      store: coupon.storeName || coupon.storeSlug,
      kind: 'partner-coupon',
    });
  }

  /**
   * Records a coupon action click against the admin Hits counter.
   *
   * Note what does NOT call this: `openOffer()`. Show Code / Get Deal open the
   * `/go/:store/:id` redirect, and that redirect already increments `hits` server-side — calling it
   * here too would score one click twice. This exists for in-modal actions that never leave the
   * page, currently the Copy button.
   */
  protected recordCouponHit(couponId: string): void {
    if (!this.isBrowser || !couponId) return;
    this.api
      .recordCouponHit(couponId)
      .pipe(
        catchError(() => of(null)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  /** Close the modal — back to the clean store URL, same page, no reload. */
  protected closeOffer(): void {
    this.router.navigate(['/', this.currentStoreSlug()]);
  }

  /** The store slug for the current route, whichever of the two store routes matched. */
  private currentStoreSlug(): string {
    return this.route.snapshot.paramMap.get('storeSlug') || this.store()?.slug || this.storeName;
  }

  /** Keep the modal + robots directive in sync with the `/coupon/:couponId` URL segment. */
  private syncOfferFromQuery(): void {
    const id = this.route.snapshot.paramMap.get('couponId');
    // The coupon URL must never be indexed: it is one store page duplicated per coupon, and the
    // canonical still points at the clean store page.
    this.seo.setNoindex(!!id);
    if (!id) {
      this.activeOffer.set(null);
      return;
    }
    const card = this.findCouponCardById(id);
    this.activeOffer.set(card ? this.toOfferData(card) : null);
  }

  private findCouponCardById(id: string): StoreCouponCard | null {
    return (
      [...this.couponCards(), ...this.expiredCouponCards()].find((card) => card.id === id) || null
    );
  }

  private offerGoUrl(couponId: string): string {
    const slug = this.route.snapshot.paramMap.get('storeSlug') || this.store()?.slug || '';
    return this.affiliate.couponUrl(slug, couponId);
  }

  private toOfferData(card: StoreCouponCard): OfferModalData {
    return {
      id: card.id,
      title: card.title,
      code: card.code || '',
      isDeal: !card.code,
      // The modal already prefixes "Ends "; strip any leading "Expires:" so it doesn't read
      // "Ends Expires: …".
      expiryText: (card.expiryText || 'Limited Time Offer').replace(/^\s*expires:\s*/i, ''),
      detail: card.description || '',
      storeName: card.affiliateStoreName || card.storeName || this.storeDisplayName(),
      storeLogoUrl: card.affiliateStoreLogoUrl || card.storeLogoUrl || '',
      storeSlug: this.route.snapshot.paramMap.get('storeSlug') || this.store()?.slug || '',
      goUrl: this.offerGoUrl(card.id),
      productsPath: '/collection',
    };
  }

  /**
   * SEO for the store page. Before this the most SEO-critical page on the site set no <title>, no
   * description, no canonical and no structured data at all.
   *
   * Runs inside the same subscribe that resolves the store, so on the server these tags are written
   * before the HTML is serialised and reach crawlers in the initial response (spec §5).
   */
  /**
   * Loads the published comparisons featuring this store and turns them into the "Compare Similar
   * Stores" cards.
   *
   * Only stores with a real published comparison get a card — the "View comparison" button has to
   * land somewhere. Previously this section was three hardcoded cards (Zaful/SHEIN/ASOS) whose
   * buttons were bare `<button>`s that did nothing at all.
   */
  /**
   * The store's discount pages, so a shopper on the coupon page can reach every one of them
   * (and each of those pages links back here — see discount-page.html).
   */
  private loadDiscountPageLinks(store: FrontendStore): void {
    const slug = store.slug || this.storeName;
    if (!slug) return;

    // Same fix as RootSlugPage's resolver: without a transfer-state cache this ran again on the
    // client straight after the server had already fetched it, so every store page cost a second,
    // publicly-visible request to /api/frontend/stores/<slug>/discount-pages — the one a renderer
    // that executes JS (Googlebot included) then shows as a URL it crawled.
    const stateKey = makeStateKey<FrontendStoreDiscountPageLink[]>(`store-discount-pages:${slug}`);
    if (this.transferState.hasKey(stateKey)) {
      this.discountPageLinks.set(this.transferState.get(stateKey, []));
      return;
    }

    const requestId = this.relatedDataRequestId;

    this.api
      .getStoreDiscountPages(slug)
      .pipe(
        catchError(() => of(null)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((response) => {
        if (requestId !== this.relatedDataRequestId || !response?.success) return;
        this.discountPageLinks.set(response.data);
        if (!this.isBrowser) this.transferState.set(stateKey, response.data);
      });
  }

  private loadComparisonCards(store: FrontendStore): void {
    const slug = store.slug || this.storeName;
    if (!slug) return;

    const requestId = this.relatedDataRequestId;

    this.api
      .getComparisons(slug, 6)
      .pipe(
        catchError(() => of(null)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((response) => {
        // Guard against an out-of-order response from a previous store (same pattern as
        // loadRelatedStoreData).
        if (requestId !== this.relatedDataRequestId || !response?.success) {
          return;
        }

        const storeId = String(store.id ?? '');
        const selfName = store.name || this.storeDisplayName();
        const selfLogoUrl = store.logoImagePath ? this.toAssetUrl(store.logoImagePath) : '';

        const cards = response.data
          .map((comparison) => {
            // Either side of the pair can be this store, so resolve both rather than assuming an
            // order — `self` is used for the left of the versus block, `other` for the right.
            const isStoreA = String(comparison.storeA.id) === storeId;
            const self = isStoreA ? comparison.storeA : comparison.storeB;
            const other = isStoreA ? comparison.storeB : comparison.storeA;
            if (!other?.slug) return null;

            const winnerName = comparison.winnerName?.trim() ?? '';

            return {
              name: other.name,
              initials: this.getInitials(other.name),
              logoUrl: other.logoImagePath ? this.toAssetUrl(other.logoImagePath) : '',
              offers: other.activeCoupons ?? null,
              selfName,
              selfInitials: this.getInitials(selfName),
              // The summary's own logo is preferred, so both sides come from one payload; the
              // store's logo is the fallback when the pair row has none.
              selfLogoUrl: self?.logoImagePath ? this.toAssetUrl(self.logoImagePath) : selfLogoUrl,
              selfOffers: self?.activeCoupons ?? null,
              categoryName: comparison.category?.name?.trim() ?? '',
              winnerName,
              selfWins: Boolean(winnerName) && winnerName === selfName,
              comparisonPath: comparison.path,
              storePath: `/${other.slug}`,
            };
          })
          .filter((card): card is StoreComparisonCard => card !== null);

        this.comparisonCards.set(cards);
      });
  }

  private applyStoreSeo(store: FrontendStore | null): void {
    if (!store) {
      // Read the cause before setNotFound consumes it. The store may exist perfectly well and the
      // API simply did not answer — saying 404 to that is what made 215 live store pages report
      // themselves as missing during the 2026-08-29 crawl.
      const unavailable = this.ssrResponse.isTransientFailure();
      // Emits 503 when the fetch failed transiently, 404 when the slug genuinely resolves to
      // nothing (spec §7 — no soft 404s).
      this.ssrResponse.setNotFound();
      this.seo.apply(
        unavailable
          ? {
              title: 'Temporarily unavailable | SavingArena',
              description: 'This page could not be loaded right now. Please try again shortly.',
              noindex: true,
            }
          : {
              title: 'Store not found | SavingArena',
              description: 'The store you are looking for is not available on SavingArena.',
              noindex: true,
            },
      );
      this.seo.setJsonLd([]);
      return;
    }

    const name = this.storeDisplayName();
    const content = this.toRecord(store.content);
    const metaTitle = this.getString(content, ['metaTitle']);
    const metaDescription = this.getString(content, ['metaDescription']);
    const couponCount = this.couponCards().length;

    // Same fallback machinery as the H1, and equally admin-editable — only the template differs.
    // It used to carry the bare year and nothing else, which said nothing about what the store is
    // actually offering right now.
    const title =
      metaTitle || this.renderStoreSeoTemplate(this.storeSeoTemplate('metaTitleFallback'));
    const description =
      metaDescription ||
      `Save with ${couponCount || 'the latest'} verified ${name} coupon codes, discounts and deals, hand-checked by SavingArena.`;

    const path = `/${store.slug || this.storeName}`;
    const canonicalUrl = this.seo.absolute(path);
    const page = this.currentProductPage();

    this.seo.apply({
      // Page 2+ gets its own title so the paginated URLs are not near-duplicates of page 1.
      title: page > 1 ? `${title} | Page ${page}` : title,
      description,
      canonicalPath: path,
      // SELF-REFERENCING, deliberately not pointing back at page 1. Canonicalising a paginated page
      // to its first page tells Google to drop it — and with it every product that is only linked
      // from there, which is the entire problem this pagination exists to solve.
      canonicalQuery: page > 1 ? `page=${page}` : '',
      image: store.logoImagePath ? this.toAssetUrl(store.logoImagePath) : undefined,
    });

    // Offer schema is only emitted for coupons actually rendered on the page (spec §8).
    // `validThrough` is deliberately omitted: the card only carries `expiryText`, a display string
    // ("Ends soon"), and schema.org requires an ISO-8601 date — emitting the display text would be
    // invalid structured data.
    const offers = this.couponCards()
      .slice(0, 20)
      .map((coupon) =>
        offerSchema({
          name: coupon.title,
          description: coupon.description || undefined,
          url: canonicalUrl,
          // The offer may belong to an affiliate store rather than the page's store.
          storeName: coupon.storeName || name,
        }),
      );

    const faqs = this.faqItems().map((faq) => ({ question: faq.question, answer: faq.answer }));

    this.seo.setJsonLd([
      webPageSchema({
        origin: this.seo.origin,
        url: canonicalUrl,
        name: title,
        description,
        dateModified: typeof store.updatedAt === 'string' ? store.updatedAt : undefined,
      }),
      breadcrumbSchemaFor(this.crumbs(), (p) => this.seo.absolute(p)),
      offers.length ? itemListSchema(offers, `${name} coupons and deals`) : null,
      // FAQPage only when the FAQs are visibly rendered — Google penalises schema for hidden content.
      faqs.length ? faqPageSchema(faqs) : null,
    ]);
  }

  protected setHoverRating(value: number): void {
    if (!this.hasRated() && !this.submittingRating()) {
      this.hoverRating.set(value);
    }
  }

  protected clearHoverRating(): void {
    this.hoverRating.set(0);
  }

  protected submitRating(stars: number): void {
    const slug = this.store()?.slug;

    if (!slug || this.hasRated() || this.submittingRating()) {
      return;
    }

    this.submittingRating.set(true);
    this.ratingError.set('');

    this.api
      .submitStoreRating(slug, stars)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response) => {
          this.submittingRating.set(false);
          this.hoverRating.set(0);
          this.applyRatingSummary(response?.data);
          this.hasRated.set(true);
          this.justRated.set(true);
        },
        error: (err: unknown) => {
          this.submittingRating.set(false);
          this.hoverRating.set(0);

          const e = err as {
            status?: number;
            error?: { data?: { rating?: number; ratingCount?: number } };
          };

          // 409 = this IP already rated (e.g. from another tab). Reflect the server's summary.
          if (e.status === 409) {
            this.applyRatingSummary(e.error?.data);
            this.hasRated.set(true);
          } else {
            this.ratingError.set('Could not submit your rating. Please try again.');
          }
        },
      });
  }

  private resetRatingState(): void {
    this.ratingAverage.set(0);
    this.ratingCount.set(0);
    this.hasRated.set(false);
    this.hoverRating.set(0);
    this.submittingRating.set(false);
    this.ratingError.set('');
    this.justRated.set(false);
  }

  private applyRatingFromStore(store: FrontendStore | null): void {
    // Only the aggregate figures come from the store payload. They are the same for everyone, so they
    // are safe to server-render and cache.
    this.ratingAverage.set(this.readRatingNumber(store?.['rating']));
    this.ratingCount.set(this.readRatingNumber(store?.['ratingCount']));

    // `hasRated` is deliberately IGNORED here — on BOTH platforms.
    //
    // It is scoped to the IP of whoever fetched the payload. Under SSR that is the SSR server, and
    // Angular's transfer cache then serialises the whole response into the HTML (`ng-state`), which
    // we cache and serve to every visitor. Reading it on the client would therefore replay the
    // *server's* rating state to everyone: if the SSR host ever rated a store (an ops curl, a probe),
    // every visitor would hydrate into "you already rated this".
    //
    // Verified in QA: the API returns hasRated=true for ::1 and false for 127.0.0.1 on the same
    // store — the value really does vary by caller, and it really was landing in shared HTML.
    //
    // So the widget always starts neutral, and `loadRatingState()` resolves the visitor's own state
    // from the browser (an endpoint that is `no-store` and never server-rendered).
    this.hasRated.set(false);
  }

  /**
   * Browser-only: fetch the visitor's own rating state and correct the neutral state that was
   * server-rendered. Never runs during SSR, so it is never captured in cached HTML.
   */
  private loadRatingState(slug: string): void {
    if (!this.isBrowser || !slug) {
      return;
    }

    this.api
      .getStoreRatingState(slug)
      .pipe(
        catchError(() => of(null)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((response) => {
        if (!response?.success) {
          return;
        }

        this.ratingAverage.set(this.readRatingNumber(response.data.rating));
        this.ratingCount.set(this.readRatingNumber(response.data.ratingCount));
        this.hasRated.set(response.data.hasRated === true);
      });
  }

  private applyRatingSummary(data?: { rating?: number; ratingCount?: number }): void {
    if (!data) {
      return;
    }

    this.ratingAverage.set(this.readRatingNumber(data.rating));
    this.ratingCount.set(this.readRatingNumber(data.ratingCount));
  }

  private readRatingNumber(value: unknown): number {
    return typeof value === 'number' && Number.isFinite(value) ? value : 0;
  }

  // --- Follow ---
  protected toggleFollow(): void {
    if (this.followed()) {
      return;
    }
    this.isFollowOpen.update((open) => !open);
  }

  protected submitFollow(): void {
    const slug = this.store()?.slug;
    const email = String(this.followForm.controls.email.value ?? '').trim();

    if (String(this.followForm.controls.companyWebsite.value ?? '').length > 0) {
      this.isFollowOpen.set(false);
      return; // honeypot tripped
    }

    if (!slug || this.followForm.invalid || this.followSubmitting()) {
      this.followForm.markAllAsTouched();
      return;
    }

    this.followSubmitting.set(true);
    this.followError.set('');

    this.api
      .followStore(slug, email)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response) => {
          this.followSubmitting.set(false);
          this.followed.set(true);
          this.isFollowOpen.set(false);
          // The badge moves from the count the API just wrote, so it can never drift from storage.
          const followers = response?.data?.followers;
          if (typeof followers === 'number') this.followerCount.set(followers);
        },
        error: (err: unknown) => {
          this.followSubmitting.set(false);
          const status = (err as { status?: number }).status;
          if (status === 409) {
            // Already following from this email — treat as success, and leave the count alone:
            // nothing was inserted server-side either.
            this.followed.set(true);
            this.isFollowOpen.set(false);
          } else {
            this.followError.set('Could not follow this store. Please try again.');
          }
        },
      });
  }

  /**
   * Share this store: OS share sheet where there is one, clipboard otherwise. Counted server-side,
   * and the badge takes the figure the API returns rather than incrementing locally.
   *
   * Dismissing the share sheet raises `AbortError` — that is not a share and must not count.
   */
  protected async shareStore(): Promise<void> {
    const slug = this.store()?.slug;
    if (!slug || !this.isBrowser) return;

    const url = `${window.location.origin}/${slug}`;

    try {
      if (navigator.share) {
        await navigator.share({ title: this.storeDisplayName(), url });
      } else {
        await navigator.clipboard.writeText(url);
        this.shareLabel.set('Link copied');
      }
    } catch (error) {
      if ((error as DOMException)?.name === 'AbortError') return;
      this.shareLabel.set('Copy failed');
      setTimeout(() => this.shareLabel.set('Share'), 2000);
      return;
    }

    setTimeout(() => this.shareLabel.set('Share'), 2000);

    this.api
      .shareStore(slug)
      .pipe(
        catchError(() => of(null)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((response) => {
        const shares = response?.data?.shares;
        if (typeof shares === 'number') this.shareCount.set(shares);
      });
  }

  // --- Leave a review ---
  protected toggleReview(): void {
    this.isReviewOpen.update((open) => !open);
  }

  protected closeReview(): void {
    this.isReviewOpen.set(false);
  }

  // Close only when the click lands on the backdrop itself, not inside the modal card.
  protected onReviewBackdrop(event: MouseEvent): void {
    if (event.target === event.currentTarget) {
      this.closeReview();
    }
  }

  protected onEscape(): void {
    if (this.isReviewOpen()) {
      this.closeReview();
    }
  }

  protected setReviewRating(value: number): void {
    this.reviewForm.controls.rating.setValue(value);
  }

  protected setReviewHover(value: number): void {
    this.reviewHover.set(value);
  }

  protected clearReviewHover(): void {
    this.reviewHover.set(0);
  }

  /** 24600 -> "24.6K", so the count never widens the button past its slot. */
  protected formatCount(value: number): string {
    return formatCompactCount(value);
  }

  protected isReviewInvalid(controlName: 'userName' | 'title' | 'description'): boolean {
    const control = this.reviewForm.get(controlName);
    return !!control && control.invalid && (control.dirty || control.touched);
  }

  protected submitReview(): void {
    const slug = this.store()?.slug;

    if (String(this.reviewForm.controls.companyWebsite.value ?? '').length > 0) {
      this.isReviewOpen.set(false);
      return; // honeypot tripped
    }

    if (!slug || this.reviewForm.invalid || this.reviewSubmitting()) {
      this.reviewForm.markAllAsTouched();
      return;
    }

    const raw = this.reviewForm.getRawValue();
    this.reviewSubmitting.set(true);
    this.reviewError.set('');

    this.api
      .submitStoreReview(slug, {
        userName: String(raw.userName ?? '').trim(),
        userDesignation: String(raw.userDesignation ?? '').trim(),
        title: String(raw.title ?? '').trim(),
        description: String(raw.description ?? '').trim(),
        rating: Number(raw.rating ?? 5),
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.reviewSubmitting.set(false);
          this.reviewSubmitted.set(true);
        },
        error: () => {
          this.reviewSubmitting.set(false);
          this.reviewError.set('Could not submit your review. Please try again.');
        },
      });
  }

  private resetFollowReviewState(): void {
    this.isFollowOpen.set(false);
    this.followSubmitting.set(false);
    this.followed.set(false);
    this.followError.set('');
    this.shareLabel.set('Share');
    this.followForm.reset({ email: '', companyWebsite: '' });

    this.isReviewOpen.set(false);
    this.reviewSubmitting.set(false);
    this.reviewSubmitted.set(false);
    this.reviewError.set('');
    this.reviewHover.set(0);
    this.reviewForm.reset({
      userName: '',
      userDesignation: '',
      title: '',
      description: '',
      rating: 5,
      companyWebsite: '',
    });
  }

  private toPageEditor(value: unknown): PageEditor | null {
    if (!value || typeof value !== 'object' || Array.isArray(value)) {
      return null;
    }

    const record = value as ApiRecord;
    const name = typeof record['name'] === 'string' ? record['name'].trim() : '';
    if (!name) {
      return null;
    }

    const shortDescription =
      typeof record['shortDescription'] === 'string' ? record['shortDescription'] : '';
    const bio = typeof record['bio'] === 'string' ? record['bio'] : '';
    const image =
      typeof record['profileImagePath'] === 'string' ? record['profileImagePath'].trim() : '';
    const profileUrl = typeof record['profileUrl'] === 'string' ? record['profileUrl'].trim() : '';

    return {
      name,
      role: typeof record['designation'] === 'string' ? record['designation'] : '',
      // shortDescription/bio are admin rich-text; strip tags and cap at 70 words ("Read more"
      // links to the author page for the full bio).
      bio: this.truncateWords(this.cleanReviewText(shortDescription || bio), 70),
      avatarUrl: image ? this.toAssetUrl(image) : '',
      initials: this.toInitials(name),
      profileUrl,
    };
  }

  private truncateWords(text: string, maxWords: number): string {
    const words = text.trim().split(/\s+/).filter(Boolean);
    if (words.length <= maxWords) {
      return text.trim();
    }
    return `${words.slice(0, maxWords).join(' ')}…`;
  }

  private toReviewItems(value: unknown): StoreReviewItem[] {
    if (!Array.isArray(value)) {
      return [];
    }

    return value
      .filter((item): item is ApiRecord => !!item && typeof item === 'object')
      .map((item) => {
        const name = typeof item['userName'] === 'string' ? item['userName'] : '';
        const rating = this.readRatingNumber(item['rating']) || 5;

        return {
          id: typeof item['id'] === 'string' ? item['id'] : name,
          title: typeof item['title'] === 'string' ? item['title'] : '',
          text:
            typeof item['description'] === 'string'
              ? this.cleanReviewText(item['description'])
              : '',
          name,
          designation: typeof item['userDesignation'] === 'string' ? item['userDesignation'] : '',
          initials: this.toInitials(name),
          dateLabel: this.formatReviewDate(item['date']),
          rating,
          stars: this.reviewRatingStars.map((star) => star),
        } satisfies StoreReviewItem;
      });
  }

  private cleanReviewText(html: string): string {
    return html
      .replace(/<[^>]*>/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }

  private toInitials(name: string): string {
    const parts = name.trim().split(/\s+/).filter(Boolean);
    if (parts.length === 0) {
      return 'SA';
    }
    const first = parts[0][0] ?? '';
    const last = parts.length > 1 ? (parts[parts.length - 1][0] ?? '') : '';
    return (first + last).toUpperCase();
  }

  private formatReviewDate(value: unknown): string {
    if (typeof value !== 'string' || value.length === 0) {
      return '';
    }
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return '';
    }
    return date.toLocaleDateString('en-US', { month: 'short', day: '2-digit', year: 'numeric' });
  }

  get aboutStoreHeading(): string {
    return `About ${this.storeDisplayName()}`;
  }

  get aboutStoreText(): string {
    return this.getStoreShortDescription(this.store()) || STORE_ABOUT_FALLBACK;
  }

  get shouldShowAboutToggle(): boolean {
    return this.aboutStoreText.length > this.aboutPreviewLimit;
  }

  // The truncating getters this block used to hold are gone. The templates print the full text
  // and CSS clamps it, so nothing is sliced out of the HTML any more. Only the "is it long enough
  // to need a toggle" decision is still made here, because CSS cannot measure its own overflow and
  // the answer has to be correct in the SSR output, before any client code runs.

  get shouldShowShippingToggle(): boolean {
    return this.zafulShippingDescription.length > this.sidebarPreviewLimit;
  }

  get visibleShippingText(): string {
    if (this.isShippingExpanded || !this.shouldShowShippingToggle) {
      return this.zafulShippingDescription;
    }

    return `${this.zafulShippingDescription.slice(0, this.sidebarPreviewLimit).trimEnd()}...`;
  }

  get shouldShowReturnsToggle(): boolean {
    return this.zafulReturnsDescription.length > this.sidebarPreviewLimit;
  }

  get visibleReturnsText(): string {
    if (this.isReturnsExpanded || !this.shouldShowReturnsToggle) {
      return this.zafulReturnsDescription;
    }

    return `${this.zafulReturnsDescription.slice(0, this.sidebarPreviewLimit).trimEnd()}...`;
  }

  get shouldShowClearanceToggle(): boolean {
    return this.zafulClearanceDescription.length > this.sidebarPreviewLimit;
  }

  get visibleClearanceText(): string {
    if (this.isClearanceExpanded || !this.shouldShowClearanceToggle) {
      return this.zafulClearanceDescription;
    }

    return `${this.zafulClearanceDescription.slice(0, this.sidebarPreviewLimit).trimEnd()}...`;
  }

  get shouldShowLongformToggle(): boolean {
    return this.longformPlainText.length > this.longformPreviewLimit;
  }

  get longformHeading(): string {
    return `The Complete ${this.storeDisplayName()} Savings Guide`;
  }

  get longformIntroText(): string {
    return `A practical savings guide for shoppers using ${this.storeDisplayName()} offers.`;
  }

  get longformHtml(): string {
    return this.getStoreLongContent(this.store()) || `<p>${STORE_LONGFORM_FALLBACK}</p>`;
  }

  get longformPlainText(): string {
    return this.htmlToText(this.longformHtml) || STORE_LONGFORM_FALLBACK;
  }

  get productsHeading(): string {
    return `Latest ${this.storeDisplayName()} Products You Should Check Out`;
  }

  get topSavingsHeading(): string {
    return `Biggest Price Drops at ${this.storeDisplayName()} Right Now`;
  }

  toggleShippingExpanded(): void {
    this.isShippingExpanded = !this.isShippingExpanded;
  }

  toggleReturnsExpanded(): void {
    this.isReturnsExpanded = !this.isReturnsExpanded;
  }

  toggleClearanceExpanded(): void {
    this.isClearanceExpanded = !this.isClearanceExpanded;
  }

  protected shouldShowSidebarWidgetToggle(widget: StoreSidebarWidgetItem): boolean {
    return widget.text.length > this.sidebarPreviewLimit;
  }

  private loadRelatedStoreData(store: FrontendStore): void {
    const requestId = ++this.relatedDataRequestId;

    this.getCompetitorStores(store)
      .pipe(
        switchMap((competitors) =>
          forkJoin({
            popularStores: this.getPopularStores(store),
            partnerCoupons: this.getPartnerCoupons(store, competitors),
            products: this.getStoreProducts(store, this.currentProductPage()),
            topSavings: this.getTopSavingsProducts(store),
            blogs: this.getStoreBlogs(store),
          }).pipe(
            map(({ popularStores, partnerCoupons, products, topSavings, blogs }) => ({
              competitors,
              popularStores,
              partnerCoupons,
              products,
              topSavings,
              blogs,
            })),
          ),
        ),
        catchError(() =>
          of({
            competitors: [],
            popularStores: [],
            partnerCoupons: [],
            products: [],
            topSavings: [],
            blogs: [],
          }),
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(({ competitors, popularStores, partnerCoupons, products, topSavings, blogs }) => {
        if (requestId !== this.relatedDataRequestId) {
          return;
        }

        this.competitorStores.set(competitors);
        this.popularStores.set(popularStores);
        this.partnerCoupons.set(partnerCoupons);
        this.storeProducts.set(products);
        this.topSavingsProducts.set(topSavings);
        this.storeBlogs.set(blogs);
        // The page count is only known once the product response lands, so prev/next and the
        // out-of-range check happen here rather than with the rest of the SEO.
        this.applyProductPaginationSeo();
      });
  }

  /**
   * `rel="prev"` / `rel="next"` across the product pages, plus the out-of-range guard.
   *
   * Page 1's prev and the last page's next are cleared rather than left stale.
   *
   * The 404 below is deliberately conditional on `total > 0`. `getStoreProducts` swallows a failed
   * request into an empty list, so a total of 0 means EITHER the store genuinely has no products OR
   * the fetch failed — and answering 404 to the second case would recreate the live-page-reports-
   * itself-missing bug that took a week to find. A page beyond a KNOWN total is a real 404; an
   * unknown total renders an empty grid with no pagination links, so nothing can discover it anyway.
   */
  private applyProductPaginationSeo(): void {
    // A 301 already owns this response. Setting a 404 on top would emit a 404 carrying a
    // Location header, which is neither one thing nor the other.
    if (this.productPageRedirected) return;
    const page = this.currentProductPage();
    const total = this.productsTotal();
    const pages = this.productTotalPages();

    if (page > pages) {
      this.seo.setPaginationLinks('', '');

      if (total === 0) {
        // A total of 0 means EITHER the store genuinely has no products OR the fetch failed, and
        // those must not both become a 404 — that is the live-page-reports-itself-missing bug.
        // But serving a self-canonical 200 instead, as the first cut did, was worse in a different
        // way: /any-store?page=50 became an indexable page of its own, an unbounded set of them.
        // A 301 to the bare store URL is the answer to both — it consolidates without ever telling
        // Google a live page is gone.
        const target = `/${this.store()?.slug ?? ''}`;
        this.ssrResponse.redirectPermanent(target);
        if (this.isBrowser) this.router.navigateByUrl(target, { replaceUrl: true });
        return;
      }

      this.ssrResponse.setNotFound();
      this.seo.apply({
        title: 'Page not found | SavingArena',
        description: 'That page of products does not exist.',
        noindex: true,
        // No canonical on a dead URL. `applyStoreSeo` has already set one pointing at the store, and
        // leaving it would nominate a 200 page as the preferred version of a 404 — the exact
        // "canonical on a non-200" condition the crawl audit verified as zero.
        noCanonical: true,
      });
      return;
    }

    const base = this.seo.absolute(`/${this.store()?.slug ?? ''}`);
    const at = (n: number) => (n <= 1 ? base : `${base}?page=${n}`);
    this.seo.setPaginationLinks(page > 1 ? at(page - 1) : '', page < pages ? at(page + 1) : '');
  }

  private toCouponCards(coupons: FrontendCoupon[], expired: boolean): StoreCouponCard[] {
    // The mapping lives with the shared card component so the discount pages build the identical
    // shape — see shared/coupon-card/coupon-card.mapper.
    return coupons
      .filter((coupon) => isCouponExpired(coupon) === expired)
      .map((coupon, index) =>
        toCouponCardView(coupon, {
          assetBaseUrl: this.apiConfig.assetBaseUrl,
          fallbackTitle: `${this.storeDisplayName()} offer`,
          fallbackStoreName: this.storeDisplayName(),
          expired,
          index,
        }),
      );
  }

  /**
   * Fills a fallback template with this store's values. Drives both the H1 and the meta title, so
   * they cannot drift apart, and it is the same substitution the admin's help text documents.
   *
   *   "{store} {discount} Coupon Codes — {month} {year}"
   *     -> "Viking Footwear 30% Off Coupon Codes — August 2026"
   *
   * `{discount}` resolves to nothing when no coupon quotes a percentage, rather than to a "0% Off"
   * that would be both wrong and worse than saying nothing. Emptying a placeholder mid-sentence
   * leaves a double space behind, so whitespace is collapsed afterwards:
   *
   *   -> "TUI NO Coupon Codes — August 2026"
   *
   * Safe to call outside a reactive context: both signals it reads are set from the same response
   * handler that goes on to call `applyStoreSeo`, so their values are already in place.
   */
  private renderStoreSeoTemplate(template: string): string {
    const percent = this.maxCouponDiscountPercent();
    const now = new Date();

    return template
      .replace(/\{store\}/g, this.storeDisplayName())
      .replace(/\{discount\}/g, percent > 0 ? `${percent}% Off` : '')
      .replace(/\{keyword\}/g, this.storeSecondaryKeyword())
      .replace(/\{month\}/g, now.toLocaleDateString('en-US', { month: 'long' }))
      .replace(/\{year\}/g, String(now.getFullYear()))
      .replace(/\s{2,}/g, ' ')
      .trim();
  }

  /**
   * The store's secondary keyword, cased for a title.
   *
   * These are free text typed once per keyword in the admin, and they arrive inconsistently cased —
   * the same phrase exists as "coupon codes", "Coupon codes" and "Coupon Codes", alongside outright
   * typos. That is data entry, not typography, so it is normalised rather than trusted: a
   * lower-case "coupon codes" sitting mid-title reads as a bug.
   *
   * A word that already carries an upper-case letter is left untouched, so an acronym or a casing an
   * admin deliberately typed survives.
   *
   * Word-by-word capitalisation is safe here because the values are noun phrases: of the 36,700
   * stores, the keywords in use are overwhelmingly "coupon codes" / "discount codes" / "promo
   * codes", and even the non-English ones ("code promo", "codice promozionale", "Kod kuponu") carry
   * no article or preposition to wrongly capitalise. A phrase that did — "code de reduction" would
   * become "Code De Reduction" — is the one case this gets mildly wrong; fix those by typing the
   * wanted casing under Stores > Secondary Names, which is then preserved.
   */
  private storeSecondaryKeyword(): string {
    const keyword = (this.store()?.secondaryKeyword ?? '').trim();
    if (!keyword) return this.DEFAULT_SECONDARY_KEYWORD;

    return keyword
      .split(/\s+/)
      .map((word) => (/[A-Z]/.test(word) ? word : word.charAt(0).toUpperCase() + word.slice(1)))
      .join(' ');
  }

  /** The admin's template for a field, or the built-in one when they have saved nothing. */
  private storeSeoTemplate(field: 'metaTitleFallback' | 'headingFallback'): string {
    const saved = this.storeSeoFallbacks()?.[field]?.trim();

    return (
      saved ||
      (field === 'metaTitleFallback' ? this.DEFAULT_TITLE_TEMPLATE : this.DEFAULT_HEADING_TEMPLATE)
    );
  }

  /**
   * The deepest percentage quoted across a batch of coupon titles, or 0 when none quote one.
   * Feeds `maxCouponDiscountPercent` — see the note there for why it reads titles and why "% back"
   * is excluded.
   */
  private maxDiscountPercentIn(cards: StoreCouponCard[]): number {
    let best = 0;

    for (const card of cards) {
      for (const match of card.title.matchAll(/(\d{1,3})\s*%(?!\s*(?:back|cash))/gi)) {
        const percent = Number(match[1]);

        if (percent > best && percent <= 100) {
          best = percent;
        }
      }
    }

    return best;
  }

  private getExpiryText(coupon: FrontendCoupon, expired = false): string {
    if (coupon.endDateText?.trim()) {
      return `${expired ? 'Expired' : 'Expires'}: ${coupon.endDateText.trim()}`;
    }

    if (coupon.endDate) {
      return `${expired ? 'Expired' : 'Expires'}: ${new Intl.DateTimeFormat('en', {
        month: 'short',
        day: '2-digit',
        year: 'numeric',
      }).format(new Date(coupon.endDate))}`;
    }

    return 'Limited Time Offer';
  }

  private isCouponExpired(coupon: FrontendCoupon): boolean {
    if (!coupon.endDate) {
      return false;
    }

    const endDate = new Date(coupon.endDate);

    if (Number.isNaN(endDate.getTime())) {
      return false;
    }

    endDate.setHours(23, 59, 59, 999);

    return endDate.getTime() < Date.now();
  }

  private toFaqItems(faqs: FrontendFaq[]): StoreFaqItem[] {
    return faqs
      .map((faq) => ({
        question: this.cleanText(faq.question),
        answer: this.cleanText(faq.answer),
      }))
      .filter((faq) => faq.question && faq.answer);
  }

  /**
   * The latest stores in this store's primary category, for the "<Category> Popular Stores" row.
   * The store-list endpoint attaches `couponsCount`/`dealsCount`, so the tiles can show a real
   * offer count instead of a hardcoded one. The current store is excluded — it is the page you
   * are already on.
   */
  private getPopularStores(store: FrontendStore): Observable<PopularStoreItem[]> {
    const category = (store.categories ?? [])[0]?.id;
    if (!category) {
      return of([]);
    }

    // The heading says "most active stores in this category right now", and until now the request
    // carried no `sortBy` at all — `buildStoreSort` returns undefined for an absent value, so this
    // was whatever order the collection happened to return. `offersUpdatedAt` is when a store's
    // coupons last actually changed, which is the one field that means what the heading claims.
    // Deliberately not `updatedAt`: that moves on every feed sync and every Follow click, so it
    // would rank by whatever the last import touched.
    return this.api
      .getStores({
        page: 1,
        limit: POPULAR_STORE_LIMIT + 1,
        category,
        sortBy: 'offersUpdatedAt',
        sortOrder: 'desc',
      })
      .pipe(
        map((response) =>
          (response.data ?? [])
            .filter((item) => item.id !== store.id)
            .slice(0, POPULAR_STORE_LIMIT)
            .map((item) => this.toPopularStoreItem(item)),
        ),
        catchError(() => of([] as PopularStoreItem[])),
      );
  }

  private toPopularStoreItem(store: FrontendStore): PopularStoreItem {
    const total = (store.couponsCount ?? 0) + (store.dealsCount ?? 0);
    const name = store.name?.trim() || 'Store';

    return {
      id: store.id,
      name,
      logoUrl: this.getStoreLogoUrl(store),
      logoInitials: this.getInitials(name),
      path: store.slug ? `/${store.slug}` : '',
      countLabel: total > 0 ? `${total}+ Coupons & Deals` : '',
    };
  }

  private getCompetitorStores(store: FrontendStore): Observable<StoreCompetitorItem[]> {
    const categoryIds = [
      ...new Set((store.categories ?? []).map((category) => category.id)),
    ].filter(Boolean);

    if (categoryIds.length === 0) {
      return of([]);
    }

    const requests = categoryIds.map((category) =>
      this.api.getStores({ page: 1, limit: COMPETITOR_LIMIT + 1, category }).pipe(
        map((response) => response.data),
        catchError(() => of([] as FrontendStore[])),
      ),
    );

    return forkJoin(requests).pipe(
      map((storeGroups) => this.toCompetitorItems(storeGroups.flat(), store)),
    );
  }

  /** Index-signature values arrive untyped; anything that is not a finite number counts as none. */
  private readNumber(value: unknown): number {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
  }

  /**
   * Writes out what a competitor is offering, in one sentence.
   *
   * Every clause is dropped when the store has nothing to put in it, so a store with deals but no
   * rating reads "… offers 4 deals with savings of up to 43% off." rather than trailing an empty
   * half-sentence. A store with nothing live says so plainly instead of being given filler.
   */
  private toCompetitorSummary(store: FrontendStore): string {
    const name = (store.name ?? '').trim() || 'This store';
    const coupons = store.couponsCount ?? 0;
    const deals = store.dealsCount ?? 0;
    const percent = this.readNumber(store['bestDiscountPercent']);
    const rating = this.readNumber(store['rating']);
    const reviews = this.readNumber(store['ratingCount']);

    const offers: string[] = [];
    if (coupons > 0) offers.push(`${coupons} ${coupons === 1 ? 'coupon' : 'coupons'}`);
    if (deals > 0) offers.push(`${deals} ${deals === 1 ? 'deal' : 'deals'}`);

    const ratingClause =
      rating > 0 && reviews > 0
        ? `a ${rating}/5 rating from ${reviews} ${reviews === 1 ? 'review' : 'reviews'}`
        : '';

    if (!offers.length) {
      return ratingClause
        ? `${name} has no live offers right now, but holds ${ratingClause}.`
        : `${name} has no live offers right now.`;
    }

    let sentence = `${name} offers ${offers.join(' and ')}`;
    if (percent > 0 && percent <= 100) sentence += ` with savings of up to ${percent}% off`;
    if (ratingClause) sentence += ` and ${ratingClause}`;

    return `${sentence}.`;
  }

  private toCompetitorItems(
    stores: FrontendStore[],
    currentStore: FrontendStore,
  ): StoreCompetitorItem[] {
    const seen = new Set<string>();
    const currentStoreKey = currentStore.id || currentStore.slug;
    const competitors: StoreCompetitorItem[] = [];

    for (const store of stores) {
      const storeKey = store.id || store.slug;

      if (!storeKey || storeKey === currentStoreKey || seen.has(storeKey)) {
        continue;
      }

      seen.add(storeKey);
      competitors.push({
        id: storeKey,
        slug: store.slug,
        name: store.name,
        domain: store.domain ?? '',
        couponsCount: store.couponsCount ?? 0,
        dealsCount: store.dealsCount ?? 0,
        // `rating` and `ratingCount` reach the model through its index signature, so they arrive
        // untyped and need coercing rather than a bare `?? 0`.
        rating: this.readNumber(store['rating']),
        ratingCount: this.readNumber(store['ratingCount']),
        summary: this.toCompetitorSummary(store),
        logoUrl: this.getStoreLogoUrl(store),
        logoInitials: this.getInitials(store.name),
        path: `/${store.slug}`,
      });

      if (competitors.length >= COMPETITOR_LIMIT) {
        break;
      }
    }

    return competitors;
  }

  private getPartnerCoupons(
    store: FrontendStore,
    competitors: StoreCompetitorItem[],
  ): Observable<StorePartnerCouponItem[]> {
    const categoryIds = [
      ...new Set((store.categories ?? []).map((category) => category.id)),
    ].filter(Boolean);

    if (categoryIds.length === 0) {
      return of([]);
    }

    const excludedStoreKeys = new Set(
      competitors.flatMap((competitor) => [competitor.id, competitor.slug]).filter(Boolean),
    );
    const requests = categoryIds.map((category) =>
      this.api.getCoupons({ page: 1, limit: PARTNER_COUPON_FETCH_LIMIT, category }).pipe(
        map((response) => response.data),
        catchError(() => of([] as FrontendCoupon[])),
      ),
    );

    return forkJoin(requests).pipe(
      map((couponGroups) => this.toPartnerCouponItems(couponGroups.flat(), excludedStoreKeys)),
    );
  }

  private toPartnerCouponItems(
    coupons: FrontendCoupon[],
    excludedStoreKeys: Set<string>,
  ): StorePartnerCouponItem[] {
    const seenCouponIds = new Set<string>();
    const seenStoreKeys = new Set<string>();
    const partnerCoupons: StorePartnerCouponItem[] = [];

    const sortedCoupons = [...coupons].sort(
      (a, b) => this.getTimestamp(b.updatedAt) - this.getTimestamp(a.updatedAt),
    );

    for (const coupon of sortedCoupons) {
      const couponStore = coupon.store ?? coupon.affiliateStore;

      if (!couponStore || seenCouponIds.has(coupon.id)) {
        continue;
      }

      const storeKey = couponStore.id || couponStore.slug;

      if (!storeKey || excludedStoreKeys.has(storeKey) || excludedStoreKeys.has(couponStore.slug)) {
        continue;
      }

      if (seenStoreKeys.has(storeKey)) {
        continue;
      }

      const title = this.cleanText(coupon.name);

      if (!title) {
        continue;
      }

      seenCouponIds.add(coupon.id);
      seenStoreKeys.add(storeKey);
      partnerCoupons.push({
        id: coupon.id,
        storeId: storeKey,
        storeSlug: couponStore.slug,
        storeName: couponStore.name,
        storeLogoUrl: this.getStoreLogoUrl(couponStore),
        storeLogoInitials: this.getInitials(couponStore.name),
        title,
        actionLabel: coupon.couponCode?.trim() ? 'Show Code' : 'Get Deal',
        isDeal: !coupon.couponCode?.trim(),
        // The shared offer card prints its own "Expires :" label, so strip the prefix here.
        expires: this.getExpiryText(coupon).replace(/^\s*expires:\s*/i, '') || 'Limited Time Offer',
        codeSuffix: (coupon.couponCode?.trim() ?? '').slice(-3),
        path: `/${couponStore.slug}`,
      });

      if (partnerCoupons.length >= PARTNER_COUPON_LIMIT) {
        break;
      }
    }

    return partnerCoupons;
  }

  private getStoreProducts(store: FrontendStore, page = 1): Observable<StoreProductItem[]> {
    const storeId = store.id;

    if (!storeId) {
      return of([]);
    }

    return this.api.getProducts({ page, limit: STORE_PRODUCT_LIMIT, store: storeId }).pipe(
      map((response) => {
        // The grid shows one page; `total` drives the pagination links and the Load more counter.
        this.productsTotal.set(Number(response.pagination?.total ?? 0));
        this.rawProductsLoaded.set(response.data?.length ?? 0);
        // Load more continues from whichever page the URL landed on, so a visitor on ?page=3 who
        // presses it gets page 4 rather than page 2.
        this.productsFetchPage.set(page);

        // `startIndex` positions the badges correctly for a page that does not start at 0.
        return this.toStoreProductItems(response.data ?? [], (page - 1) * STORE_PRODUCT_LIMIT);
      }),
      catchError(() => of([])),
    );
  }

  /**
   * Reloads only the product grid when `?page=` changes, leaving the store, its coupons and every
   * other block alone. Client-side page navigation therefore costs one request, not a full reload.
   *
   * The first emission is skipped: the initial page is already read from the route snapshot and
   * fetched by `loadRelatedStoreData`, so acting on it here would fire the same request twice.
   */
  private watchProductPage(): void {
    let first = true;
    this.route.queryParamMap
      .pipe(
        map((query) => {
          const page = this.normalizeProductPage(query.get('page'));
          // 301 anything that is not the canonical spelling of its page, rather than serving it as
          // an indexable 200 and relying on the canonical alone. `?page=abc`, `?page=0`, `?page=-3`
          // and `?page=1` are an unbounded supply of distinct URLs that all render page 1; a
          // redirect collapses them at the HTTP layer so they never enter the index at all.
          this.redirectMalformedPage(query, page);
          return page;
        }),
        distinctUntilChanged(),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((page) => {
        this.currentProductPage.set(page);
        if (first) {
          first = false;
          return;
        }
        const store = this.store();
        if (!store) return;
        this.getStoreProducts(store, page)
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe((products) => this.storeProducts.set(products));
      });
  }

  /**
   * Anything that is not an integer above 1 becomes page 1.
   *
   * This is what stops the pagination minting unbounded URLs: `?page=abc`, `?page=0` and `?page=-3`
   * all collapse to page 1, whose canonical is the bare store URL, so they cannot become separate
   * indexable pages.
   */
  private normalizeProductPage(value: string | null): number {
    const parsed = Number.parseInt(String(value ?? '1'), 10);
    return Number.isFinite(parsed) && parsed > 1 ? parsed : 1;
  }

  /**
   * 301s a `page` value that is not already the canonical spelling of the page it resolves to.
   *
   *   ?page=abc, ?page=0, ?page=-3, ?page=1  ->  the bare store URL
   *   ?page=2.5, ?page=02                    ->  ?page=2
   *   ?page=2                                ->  left alone
   *
   * Other query parameters are PRESERVED. A redirect that dropped `utm_source` would break campaign
   * attribution to make an SEO point; the tracking params are instead kept out of the canonical,
   * which is where they actually cause duplication.
   */
  private redirectMalformedPage(query: ParamMap, page: number): void {
    this.productPageRedirected = false;
    const raw = query.get('page');
    if (raw === null) return;

    // A value too large to be represented exactly (`?page=999999999999999999` parses to 1e18) has no
    // canonical spelling to redirect to — String() of it differs from the input, so we would emit a
    // 301 to a URL that then 404s. Leave it to the out-of-range check for a clean single 404.
    if (!Number.isSafeInteger(page)) return;

    const canonical = page > 1 ? String(page) : null;
    if (raw === canonical) return;

    const params = new URLSearchParams();
    for (const key of query.keys) {
      if (key === 'page') continue;
      for (const value of query.getAll(key)) params.append(key, value);
    }
    if (canonical) params.set('page', canonical);

    const path = this.router.url.split('?')[0];
    const qs = params.toString();
    const target = qs ? `${path}?${qs}` : path;

    this.productPageRedirected = true;
    this.ssrResponse.redirectPermanent(target);
    // The SSR call is a no-op in the browser, so the client needs its own navigation to land on the
    // same URL a crawler would have been sent to.
    if (this.isBrowser) this.router.navigateByUrl(target, { replaceUrl: true });
  }

  /**
   * Appends the next page of this store's active products, so a store with thousands of them is not
   * capped at the first batch. Deduped by id, like the coupon list.
   */
  protected loadMoreProducts(): void {
    const store = this.store();
    if (!store || this.loadingMoreProducts() || !this.hasMoreProducts()) {
      return;
    }

    const storeId = String(store.id ?? '');
    if (!storeId) return;

    const nextPage = this.productsFetchPage() + 1;
    this.loadingMoreProducts.set(true);

    this.api
      .getProducts({ store: storeId, page: nextPage, limit: STORE_PRODUCT_LIMIT })
      .pipe(
        catchError(() => of(null)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((response) => {
        this.loadingMoreProducts.set(false);
        if (!response?.success) return;

        const fetched = response.data ?? [];
        this.productsFetchPage.set(nextPage);
        this.rawProductsLoaded.update((count) => count + fetched.length);

        const fresh = this.toStoreProductItems(fetched, this.storeProducts().length);
        if (fresh.length) this.storeProducts.update((products) => [...products, ...fresh]);
      });
  }

  // `startIndex` is the position of this batch within the whole grid, not within the batch: the
  // "New In" / "Trending" badges key off index 0 and 5, so a second page starting at 0 would stamp a
  // duplicate "New In" halfway down the list.
  private mapProductItems(products: FrontendProduct[], startIndex = 0): StoreProductItem[] {
    return products
      .map((product, index) => this.toStoreProductItem(product, startIndex + index))
      .filter((product): product is StoreProductItem => product !== null);
  }

  // Paginated grid only. The id set guards against a page boundary re-rendering the same card, so it
  // must NOT be applied to other sections — the savings row deliberately shows products that also
  // appear in the grid above, and sharing the set would silently blank whichever rendered second.
  private toStoreProductItems(products: FrontendProduct[], startIndex = 0): StoreProductItem[] {
    return this.mapProductItems(products, startIndex).filter((product) => {
      if (this.loadedProductIds.has(product.id)) return false;
      this.loadedProductIds.add(product.id);
      return true;
    });
  }

  /** The store's deepest markdowns, ranked by the API on `discountPercent`. */
  private getTopSavingsProducts(store: FrontendStore): Observable<StoreProductItem[]> {
    const storeId = store.id;
    if (!storeId) return of([]);

    return this.api
      .getProducts({ page: 1, limit: STORE_TOP_SAVINGS_LIMIT, store: storeId, sort: 'discount' })
      .pipe(
        map((response) => this.mapProductItems(response.data ?? [])),
        catchError(() => of([])),
      );
  }

  private toStoreProductItem(product: FrontendProduct, index: number): StoreProductItem | null {
    const title = this.cleanText(product.title ?? product.name);
    const imagePath = product.thumbnailImagePath || product.galleryImagePaths?.[0] || '';

    if (!title || !imagePath) {
      return null;
    }

    return {
      id: product.id,
      title,
      description: this.cleanText(product.description ?? ''),
      imageUrl: this.toAssetUrl(imagePath),
      // galleryImagePaths holds alternate angles only, so the first entry is normally a real change.
      // The exception is a product with no thumbnail, where that entry is already the main image —
      // then the hover has to come from the next one, or there is nothing to swap to.
      hoverImageUrl: this.toHoverImageUrl(product, imagePath),
      // `newPrice` first: it is the Product model's own field and what every network sync writes.
      // The rest are legacy/importer aliases kept so older records still render.
      price: this.getProductPrice(product, [
        'newPrice',
        'price',
        'salePrice',
        'currentPrice',
        'discountedPrice',
      ]),
      oldPrice: this.getProductPrice(product, [
        'oldPrice',
        'regularPrice',
        'originalPrice',
        'compareAtPrice',
      ]),
      path: `/${this.getProductRouteKey(product)}`,
      buyHref: this.getProductBuyHref(product),
      tag:
        index === 0
          ? 'New In'
          : index === 5
            ? 'Trending'
            : index === 3 || index === 7
              ? 'Limited'
              : '',
      isLimited: index === 3 || index === 7,
    };
  }

  private getStoreBlogs(store: FrontendStore): Observable<StoreBlogItem[]> {
    const storeKey = store.slug || store.id;

    if (!storeKey) {
      return of([]);
    }

    // `store` is filtered server-side, so this asks for exactly what it renders. It used to pull 60
    // full blogs (~215 KB of transfer state) and match them in the browser — for most stores that
    // shipped the entire payload to render nothing.
    //
    // Blogs assigned to this store in the admin win. Almost no store has one (the field is opt-in
    // and there are tens of thousands of stores), so the fallback is the store's own coupon
    // category — topically relevant guides instead of a heading with nothing under it.
    return this.api.getBlogs({ page: 1, limit: STORE_BLOG_LIMIT, store: storeKey, fields: 'card' }).pipe(
      switchMap((response) => {
        if (response.data.length > 0) {
          return of(response.data);
        }

        const categoryId = (store.categories ?? []).map((category) => category.id).find(Boolean);

        if (!categoryId) {
          return of([] as FrontendBlog[]);
        }

        return this.api
          .getBlogs({ page: 1, limit: STORE_BLOG_LIMIT, couponCategory: categoryId, fields: 'card' })
          .pipe(
            map((categoryResponse) => categoryResponse.data),
            catchError(() => of([] as FrontendBlog[])),
          );
      }),
      map((blogs) => this.toStoreBlogItems(blogs, store)),
      catchError(() => of([])),
    );
  }

  private toStoreBlogItems(blogs: FrontendBlog[], store: FrontendStore): StoreBlogItem[] {
    return [...blogs]
      .sort((a, b) => this.getBlogTimestamp(b) - this.getBlogTimestamp(a))
      .map((blog) => this.toStoreBlogItem(blog, store))
      .filter((blog): blog is StoreBlogItem => blog !== null)
      .slice(0, STORE_BLOG_LIMIT);
  }

  private toStoreBlogItem(blog: FrontendBlog, store: FrontendStore): StoreBlogItem | null {
    const title = this.cleanText(blog.name);
    const slug = this.cleanText(blog.slug);

    if (!title || !slug) {
      return null;
    }

    const categoryName = this.cleanText(blog.category?.name ?? '') || 'Shopping Tips';
    const categorySlug = this.cleanText(blog.category?.slug ?? '') || this.slugify(categoryName);
    const readTimeMinutes = this.getReadTimeMinutes(blog);

    return {
      id: blog.id || slug,
      title,
      excerpt: this.getBlogExcerpt(blog),
      imageUrl: blog.thumbnailImagePath
        ? this.toAssetUrl(blog.thumbnailImagePath)
        : '/images/blog-placeholder.svg',
      imageAlt: title,
      // These are guides assigned to this store, so show this store's logo on the card.
      storeLogoUrl: this.getStoreLogoUrl(store),
      path: ['/blogs', slug],
      categoryName,
      categoryPath: ['/blogs', categorySlug],
      authorName: 'SavingArena Team',
      authorPath: ['/experts', 'savingarena-team'],
      readTime: `${readTimeMinutes} Min`,
    };
  }

  private getBlogExcerpt(blog: FrontendBlog): string {
    const shortDescription = this.cleanText(blog.shortDescription ?? '');

    if (shortDescription) {
      return shortDescription;
    }

    const content = this.cleanText(blogBodyText(blog));

    if (!content) {
      return blog.name;
    }

    return content.length <= 150 ? content : `${content.slice(0, 150).trimEnd()}...`;
  }

  private getReadTimeMinutes(blog: FrontendBlog): number {
    return blogReadMinutes(blog);
  }

  private getBlogTimestamp(blog: FrontendBlog): number {
    return this.getTimestamp(blog.publishAt) || this.getTimestamp(blog.updatedAt);
  }

  private getProductRouteKey(product: FrontendProduct): string {
    const slug = product['slug'];

    return typeof slug === 'string' && slug.trim() ? slug.trim() : product.id;
  }

  /**
   * Where "Buy Now" sends the shopper. The product's own affiliate URL when the feed carries one,
   * otherwise the store's — a store-level link still lands on the right merchant, just not the
   * exact product. Empty when neither exists, which hides the action rather than linking nowhere.
   */
  private getProductBuyHref(product: FrontendProduct): string {
    const productLink =
      typeof product.affiliateLink === 'string' ? product.affiliateLink.trim() : '';
    if (productLink) return productLink;

    const store = product.store as { affiliateLink?: string } | undefined;
    const storeLink = typeof store?.affiliateLink === 'string' ? store.affiliateLink.trim() : '';

    return storeLink;
  }

  private toHoverImageUrl(product: FrontendProduct, mainImagePath: string): string {
    const alternate = (product.galleryImagePaths ?? []).find(
      (path) => path && path !== mainImagePath,
    );

    return alternate ? this.toAssetUrl(alternate) : '';
  }

  private getProductPrice(product: FrontendProduct, keys: string[]): string {
    for (const key of keys) {
      const value = product[key];

      if (typeof value === 'number' && Number.isFinite(value)) {
        return `$${value.toFixed(2)}`;
      }

      if (typeof value === 'string' && value.trim()) {
        return value.trim();
      }
    }

    return '';
  }

  private toMembershipSection(store: FrontendStore | null): StoreMembershipSection {
    const membershipPlans = this.toRecord(store?.['membershipPlans']);
    const rawPlans = membershipPlans?.['plans'];
    const plans = Array.isArray(rawPlans)
      ? rawPlans
          .map((plan, index) => this.toMembershipPlanItem(plan, index, store))
          .filter((plan): plan is StoreMembershipPlanItem => plan !== null)
      : [];

    return {
      heading:
        this.getString(membershipPlans, ['heading', 'title']) ||
        `${this.storeDisplayName()} Best Plans`,
      description: this.getString(membershipPlans, ['description', 'shortDescription', 'subtitle']),
      plans,
    };
  }

  /**
   * One plan card from the API's `membershipPlans.plans[]` row.
   *
   * The row is the admin's Membership Plan record as `serializeMembershipPlans` sends it:
   * `planName`, `planShortDescription`, a numeric `price` with a separate `currencySymbol`,
   * `billingPeriod` ("/month"), `extraBillingText`, `discountBadge` ("50% OFF"), `badgeText`,
   * `buttonText`, `buttonStyle` ("filled" marks the plan the admin wants to stand out) and
   * `affiliateLink`. The earlier key names here came from the page's mock and matched none of
   * those, so the first real plans rendered with a bare number for a price, no period and an
   * inert button. The mock names are kept as fallbacks so nothing that used them breaks.
   */
  private toMembershipPlanItem(
    value: unknown,
    index: number,
    store: FrontendStore | null,
  ): StoreMembershipPlanItem | null {
    const plan = this.toRecord(value);

    if (!plan) {
      return null;
    }

    const name = this.getString(plan, ['name', 'title', 'planName']);
    const rawPrice = plan['price'] ?? plan['amount'] ?? plan['salePrice'] ?? plan['currentPrice'];
    const currency = this.getString(plan, ['currencySymbol', 'currency']);
    // A numeric price is shown to two decimals behind the admin's symbol ("$2.99"); a string price
    // is trusted as already formatted.
    const price =
      typeof rawPrice === 'number' && Number.isFinite(rawPrice) && rawPrice > 0
        ? `${currency}${rawPrice.toFixed(2)}`
        : typeof rawPrice === 'string' && rawPrice.trim()
          ? this.cleanText(rawPrice)
          : '';

    if (!name && !price) {
      return null;
    }

    const featured =
      this.getBoolean(plan, ['featured', 'isFeatured', 'popular', 'isPopular']) ||
      this.getString(plan, ['buttonStyle']) === 'filled';
    const badge =
      this.getString(plan, ['badge', 'badgeText', 'label']) || (featured ? 'Most Popular' : '');

    // The store click-out (`/go/s/<slug>`), the same door every other button on this page uses:
    // the redirect is counted, and it answers identically for a crawler and a visitor. The
    // plan row does carry its own `affiliateLink`, but nothing routes a per-plan destination
    // yet, and printing that raw URL here would bypass the click accounting and expose the
    // tracking link in the HTML, which no other surface does. Until `/go/` learns plans, every
    // plan button leaves through the store.
    const href = this.affiliate.storeUrl(store?.slug ?? '');

    return {
      id: this.getString(plan, ['id', '_id']) || `${name || 'plan'}-${index}`,
      name: name || `Plan ${index + 1}`,
      description: this.getString(plan, ['planShortDescription', 'description', 'shortDescription', 'subtitle']),
      price,
      period: this.getString(plan, ['billingPeriod', 'period', 'interval', 'duration', 'billingCycle']),
      discount: this.getString(plan, ['discountBadge', 'discount']),
      features: this.toMembershipFeatures(plan['features'] ?? plan['benefits'] ?? plan['items']),
      buttonText: this.getString(plan, ['buttonText', 'ctaText', 'ctaLabel']) || `Choose ${name}`,
      footnote: this.getString(plan, ['extraBillingText', 'footnote', 'note', 'billingNote']),
      badge,
      featured,
      href,
    };
  }

  private toMembershipFeatures(value: unknown): StoreMembershipFeature[] {
    if (!Array.isArray(value)) {
      return [];
    }

    return value
      .map((feature) => {
        if (typeof feature === 'string') {
          return { text: this.cleanText(feature), muted: false };
        }

        const record = this.toRecord(feature);

        if (!record) {
          return null;
        }

        return {
          text: this.getString(record, ['text', 'name', 'label', 'title']),
          muted: this.getBoolean(record, ['muted', 'disabled', 'isMuted', 'isDisabled']),
        };
      })
      .filter((feature): feature is StoreMembershipFeature => Boolean(feature?.text));
  }

  private getString(record: ApiRecord | null | undefined, keys: string[]): string {
    if (!record) {
      return '';
    }

    for (const key of keys) {
      const value = record[key];

      if (typeof value === 'string' && value.trim()) {
        return this.cleanText(value);
      }

      if (typeof value === 'number' && Number.isFinite(value)) {
        return String(value);
      }
    }

    return '';
  }

  private getBoolean(record: ApiRecord, keys: string[]): boolean {
    for (const key of keys) {
      const value = record[key];

      if (typeof value === 'boolean') {
        return value;
      }
    }

    return false;
  }

  private toSidebarWidgetItems(widgets: unknown[]): StoreSidebarWidgetItem[] {
    return widgets
      .map((widget, index) => this.toSidebarWidgetItem(widget, index))
      .filter((widget): widget is StoreSidebarWidgetItem => widget !== null);
  }

  private toSidebarWidgetItem(value: unknown, index: number): StoreSidebarWidgetItem | null {
    const widget = this.toRecord(value);

    if (!widget) {
      return null;
    }

    const title = this.getString(widget, ['title', 'heading', 'name', 'label']);
    const text = this.getString(widget, ['content', 'description', 'text', 'body', 'html']);

    if (!title || !text) {
      return null;
    }

    return {
      id: this.getString(widget, ['id', '_id']) || `${title}-${index}`,
      title,
      text,
    };
  }

  private toSupportLinkItems(value: unknown): StoreSupportLinkItem[] {
    if (!Array.isArray(value)) {
      return [];
    }

    return value
      .map((item, index) => this.toSupportLinkItem(item, index))
      .filter((item): item is StoreSupportLinkItem => item !== null);
  }

  private toSupportLinkItem(value: unknown, index: number): StoreSupportLinkItem | null {
    const link = this.toRecord(value);

    if (!link) {
      return null;
    }

    const title = this.getString(link, ['title', 'label', 'name']);

    if (!title) {
      return null;
    }

    const iconValue = this.getString(link, ['icon', 'iconClass', 'iconCode']);

    return {
      id: this.getString(link, ['id', '_id']) || `${title}-${index}`,
      title,
      description: this.getString(link, ['description', 'text', 'subtitle']),
      url: this.getString(link, ['url', 'href', 'link']) || '#',
      icon: this.getSupportIcon(iconValue, title),
    };
  }

  private toSocialLinkItems(value: unknown): StoreSocialLinkItem[] {
    if (!Array.isArray(value)) {
      return [];
    }

    return value
      .map((item, index) => this.toSocialLinkItem(item, index))
      .filter((item): item is StoreSocialLinkItem => item !== null);
  }

  private toSocialLinkItem(value: unknown, index: number): StoreSocialLinkItem | null {
    const link = this.toRecord(value);

    if (!link) {
      return null;
    }

    const iconValue = this.getString(link, ['icon', 'iconClass', 'name']);
    const url = this.getString(link, ['url', 'href', 'link']) || '#';

    return {
      id: this.getString(link, ['id', '_id']) || `${iconValue || 'social'}-${index}`,
      url,
      label: this.getSocialLabel(iconValue, url),
      icon: this.getSocialIcon(iconValue),
    };
  }

  private toInternalLinkStoreItems(value: unknown): StoreInternalLinkItem[] {
    const internalLinks = this.toRecord(value);
    const stores = internalLinks?.['linkingStores'];

    if (!Array.isArray(stores)) {
      return [];
    }

    return stores
      .map((store, index) => this.toInternalLinkStoreItem(store, index))
      .filter((store): store is StoreInternalLinkItem => store !== null);
  }

  private toInternalLinkStoreItem(value: unknown, index: number): StoreInternalLinkItem | null {
    const store = this.toRecord(value);

    if (!store) {
      return null;
    }

    const name = this.getString(store, ['name', 'title']);
    const slug = this.getString(store, ['slug']);

    if (!name || !slug) {
      return null;
    }

    const logoImagePath = this.getString(store, ['logoImagePath', 'thumbnailImagePath']);

    return {
      id: this.getString(store, ['id', '_id']) || `${slug}-${index}`,
      name,
      path: `/${slug}`,
      logoUrl: logoImagePath ? this.toAssetUrl(logoImagePath) : '',
      logoInitials: this.getInitials(name),
      couponCountText: this.getCouponCountText(store['totalCoupons']),
    };
  }

  private getCouponCountText(value: unknown): string {
    if (typeof value === 'number' && Number.isFinite(value)) {
      return `${value} ${value === 1 ? 'Coupon' : 'Coupons'} & Deals`;
    }

    if (typeof value === 'string' && value.trim()) {
      const count = Number(value.trim());

      if (Number.isFinite(count)) {
        return `${count} ${count === 1 ? 'Coupon' : 'Coupons'} & Deals`;
      }
    }

    return 'Coupons & Deals';
  }

  private getSupportIcon(iconValue: string, title: string): StoreSupportIcon {
    const value = `${iconValue} ${title}`.toLowerCase();

    if (/return|refund|swap|exchange/.test(value)) {
      return 'returns';
    }

    if (/secure|payment|shield|lock/.test(value)) {
      return 'payment';
    }

    if (/box|ship|delivery|package/.test(value)) {
      return 'shipping';
    }

    return 'default';
  }

  private getSocialIcon(iconValue: string): StoreSocialIcon {
    const value = iconValue.toLowerCase();

    if (value.includes('facebook')) {
      return 'facebook';
    }

    if (value.includes('instagram')) {
      return 'instagram';
    }

    if (value.includes('twitter') || value.includes('x-line')) {
      return 'x';
    }

    if (value.includes('youtube')) {
      return 'youtube';
    }

    if (value.includes('linkedin')) {
      return 'linkedin';
    }

    if (value.includes('pinterest')) {
      return 'pinterest';
    }

    return 'default';
  }

  private getSocialLabel(iconValue: string, url: string): string {
    const icon = this.getSocialIcon(iconValue);

    if (icon !== 'default') {
      return icon === 'x' ? 'X' : `${icon.charAt(0).toUpperCase()}${icon.slice(1)}`;
    }

    return url && url !== '#' ? url : 'Social profile';
  }

  protected getExternalTarget(url: string): string | null {
    return /^https?:\/\//i.test(url) ? '_blank' : null;
  }

  protected getExternalRel(url: string): string | null {
    return /^https?:\/\//i.test(url) ? 'noopener noreferrer' : null;
  }

  private toRecord(value: unknown): ApiRecord | null {
    return value !== null && typeof value === 'object' && !Array.isArray(value)
      ? (value as ApiRecord)
      : null;
  }

  private getTimestamp(value: string | undefined): number {
    return value ? new Date(value).getTime() || 0 : 0;
  }

  private getStoreLogoUrl(store: FrontendStore): string {
    if (store.logoImagePath) {
      return this.toAssetUrl(store.logoImagePath);
    }

    return '';
  }

  /** Event slugs carry their season ("halloween-sale", "black-friday"), which is enough to colour the ribbon. */
  private eventRibbonTheme(slug: string): StoreEventRibbonTheme {
    const value = slug.toLowerCase();
    if (value.includes('halloween')) return 'halloween';
    if (value.includes('black-friday') || value.includes('black-week')) return 'black-friday';
    if (value.includes('cyber')) return 'cyber-monday';
    if (value.includes('singles')) return 'singles-day';
    if (value.includes('christmas') || value.includes('xmas')) return 'christmas';
    return 'default';
  }

  private getInitials(value: string): string {
    const words = value
      .split(/\s+/)
      .map((word) => word.trim())
      .filter(Boolean);

    if (words.length === 0) {
      return 'SA';
    }

    if (words.length === 1) {
      return words[0].slice(0, 2).toUpperCase();
    }

    return words
      .slice(0, 2)
      .map((word) => word[0])
      .join('')
      .toUpperCase();
  }

  private getStoreShortDescription(store: FrontendStore | null): string {
    if (!store) {
      return '';
    }

    return (
      this.getContentString(store.content, 'shortDescription') ||
      this.cleanText(store.topPickShortDescription ?? '') ||
      this.getContentString(store.content, 'metaDescription')
    );
  }

  private getStoreLongContent(store: FrontendStore | null): string {
    if (!store) {
      return '';
    }

    return (
      this.getContentHtml(store.content, 'longContent') ||
      this.getContentHtml(store.content, 'longDescription') ||
      this.getContentHtml(store.content, 'description')
    );
  }

  private getDiscountBadgeFontSize(value: string): string | null {
    const length = value.replace(/\s+/g, '').length;

    if (length <= 4) {
      return null;
    }

    const fontSizeInRem = Math.max(1.35, Math.min(2.55, 12 / length));

    return `${fontSizeInRem.toFixed(3)}rem`;
  }

  private getPillBadgeFontSize(value: string): string | null {
    const length = value.replace(/\s+/g, '').length;

    if (length <= 8) {
      return null;
    }

    const fontSizeInPx = Math.max(11, Math.min(15, 92 / length));

    return `${fontSizeInPx.toFixed(1)}px`;
  }

  private getContentString(content: Record<string, unknown> | undefined, key: string): string {
    const value = content?.[key];

    return typeof value === 'string' ? this.cleanText(value) : '';
  }

  private getContentHtml(content: Record<string, unknown> | undefined, key: string): string {
    const value = content?.[key];

    if (typeof value !== 'string' || !this.cleanText(value)) {
      return '';
    }

    return value.trim();
  }

  private htmlToText(value: string): string {
    return this.cleanText(value);
  }

  private cleanText(value: string): string {
    return value
      .replace(/<br\s*\/?>/gi, ' ')
      .replace(/<[^>]+>/g, ' ')
      .replace(/&nbsp;/g, ' ')
      .replace(/&amp;/g, '&')
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/&quot;/g, '"')
      .replace(/&#39;/g, "'")
      .replace(/\s+/g, ' ')
      .trim();
  }

  private slugify(value: string): string {
    return value
      .toLowerCase()
      .replace(/&/g, 'and')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  }

  private toAssetUrl(path: string): string {
    if (/^https?:\/\//i.test(path)) {
      return path;
    }

    const assetBaseUrl = this.apiConfig.assetBaseUrl.replace(/\/$/, '');
    const assetPath = path.startsWith('/') ? path : `/${path}`;

    return `${assetBaseUrl}${assetPath}`;
  }
}
